import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MuiTextField } from '../../../../../components/MuiComponents/FormInputs';
import { FormControl, FormControlLabel, Radio, RadioGroup, MenuItem, Slider } from '@mui/material';
import Button from '../../../../../components/Button';
import Typography from '../../../../../components/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SettingsIcon from '@mui/icons-material/Settings';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { useForm, useFieldArray } from 'react-hook-form';
import MuiTooltip from '../../../../../components/MuiComponents/MuiTooltip';
import Info from '../../../../../components/Info';
import { useIntegrationDetails } from '../../../../../hooks/integrations';
import ProviderType from '../../../../../utils/constants/integrations';
import SearchIcon from '../../../../../components/SvgIcons/SearchIcon';
import { LayoutContext } from '../../../../../Providers/LayoutProvider';
import { MESSAGE_TYPES, MESSAGE_SOURCES, getMessageText } from '../messageConfig';
import PublicIcon from '@mui/icons-material/Public';
import HandymanIcon from '@mui/icons-material/Handyman';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import GreenCheck from '../../../../../components/SvgIcons/GreenCheck';
import ScheduleTest from './ScheduleTest';
import APICollectionSourceWidget from './APICollectionSourceWidget';
import DeclarationModal from './DeclarationModal';
import { getCanonicalGenptTargetUrl } from '../utils/getCanonicalGenptTargetUrl';
import { normalizeScheduleModalCollectionBlob } from '../utils/buildGenptSchedulePayload';

const normalizeVpn = (s) => (s || '').toLowerCase().trim();

function APIConfigurationForm({
  msg = null,
  targetURL = null,
  testConfiguration = null,
  chatId = null,
  sessionId = null,
  sessionDetails = null,
  chatMessages = null,
  // Legacy props for backward compatibility (when used outside chat)
  open = true,
  setOpen = () => {},
  validateTargetData = null,
  handleStartTest = null,
  onConfigCompleted = null,
  /** When true, do not emit genpt:addMessage (e.g. Schedule GenPT modal outside chat). */
  suppressDeclarationEvent = false,
  /** When true (e.g. Schedule GenPT modal), omit outer card border/shadow — dialog already frames content. */
  embeddedInModal = false,
  isReadOnly: legacyIsReadOnly = false,
  submittedDataProp: legacySubmittedDataProp = null,
  /** Schedule GenPT modal: user confirmed cancel on declaration — close parent dialog. */
  onDeclarationDiscard = null,
  /**
   * In modals: when false, schedule step shows "Schedule Test" (pick-asset-first new schedule, ROAR-4328).
   * When true, or whenever `embeddedInModal` is false (GenPT chat API start-test wizard), label is "Confirm and Save".
   */
  scheduleSubmitConfirmAndSave = true,
  /** ROAR-4341: when editing a schedule, exclude this id from same-time conflict checks */
  editingScheduleId = null,
  /**
   * CI/CD pipeline: after API collection step in the schedule modal, merge primary collection URL
   * and schema path into parent pipeline config (`apiCollectionURL`, `schemaEndpointPath`).
   */
  onPipelineCollectionConfigured = null,
  /** When true (e.g. Schedule GenPT modal with `source="pipeline"`), omit the Schedule step; throttling saves via `onPipelineThrottleSave`. */
  hideScheduleTab = false,
  /** CI/CD pipeline API: persist test + throttle from modal (same contract as `TestConfigurationForm`). */
  onPipelineThrottleSave = null,
  /** Activities “View Setup Details”: expand read-only API widget by default (parity with TestConfigurationForm). */
  readOnlyDefaultExpanded = false,
  readOnlyUseModalHeader = false,
  /** When set with `readOnlyUseModalHeader`, closes parent (e.g. Activities modal). */
  onReadOnlyModalClose = null,
}) {
  // Extract values from msg if provided (chat usage), otherwise use legacy props
  const isReadOnly = msg ? (msg?.status === 'completed') : legacyIsReadOnly;
  const readOnlyAlwaysOpen = Boolean(readOnlyUseModalHeader && readOnlyDefaultExpanded);
  /**
   * Chat: prefer `msg.widget.data`, then `testConfiguration`. Schedule GenPT modal passes a synthetic
   * `msg` (no widget) + edit prefill as `submittedDataProp` — must fall through to that legacy prop
   * or GET prefill is ignored and the schedule step stays empty (ROAR-4387).
   */
  const submittedDataProp = msg
    ? (msg?.widget?.data ?? testConfiguration ?? legacySubmittedDataProp ?? null)
    : legacySubmittedDataProp;
  const assetNetworkAccess = msg?.metadata?.asset?.network_access;
  const isInternalAsset = assetNetworkAccess === 'internal';

  // Merge crm_type from metadata into validateTargetData (plain derivation — not useMemo — so
  // eslint react-hooks/rules-of-hooks does not treat the first hook call as "conditional" in CI)
  const crmTypeFromMeta = msg?.metadata?.crm_type;
  const mergedValidateTargetData = crmTypeFromMeta
    ? { ...(validateTargetData || {}), crm_type: crmTypeFromMeta }
    : validateTargetData;

  // All hooks must be called before any conditional returns
  const formOptions = useForm({
    mode: 'onChange',
    defaultValues: {
      customHeaders: [{ headerName: '', headerValue: '' }],
    },
  });
  const {
    register,
    formState: { errors },
    handleSubmit,
    watch,
    reset,
    setValue,
    control,
  } = formOptions;

  const {
    fields: headerFields,
    append: appendHeader,
    remove: removeHeader,
    replace: replaceHeaders,
  } = useFieldArray({
    control,
    name: 'customHeaders',
  });

  const replaceHeadersRef = useRef(replaceHeaders);
  replaceHeadersRef.current = replaceHeaders;
  const stableReplaceHeaders = useCallback((...args) => {
    replaceHeadersRef.current(...args);
  }, []);

  const navigate = useNavigate();
  const { mainUrl } = useContext(LayoutContext);

  // Form field watches - watch form values for reactivity
  const cookieAuth = watch('cookieAuth') || '';
  // Watch form values to ensure reactivity when fields change
  const formValues = watch();

  // State management
  const [showAuthFields, setShowAuthFields] = useState(false);
  const [networkType, setNetworkType] = useState(isInternalAsset ? 'internal' : 'external');
  const [selectedVpnProfile, setSelectedVpnProfile] = useState(null);
  const [continueIsLoading, setContinueIsLoading] = useState(false);
  /** ROAR-4364 / ROAR-4384: schedule modal always begins on collection (create + edit). */
  const [activeStep, setActiveStep] = useState(() =>
    embeddedInModal ? 'collection' : 'configuration'
  ); // 'collection' | 'configuration' | 'throttling' | 'schedule'
  /** Set when user clicks "Start & Schedule" — used when submitting the Schedule step */
  const [throttlingConfigForSchedule, setThrottlingConfigForSchedule] = useState(null);
  /** Normalized api_config from collection step (URLs/files/schema) — merged into schedule test_config. */
  const [scheduleModalApiCollection, setScheduleModalApiCollection] = useState(() =>
    embeddedInModal ? normalizeScheduleModalCollectionBlob(legacySubmittedDataProp) : null
  );

  const scheduleModalCollectionSessionDetails = useMemo(() => {
    if (!embeddedInModal || !scheduleModalApiCollection) return null;
    return { api_config: scheduleModalApiCollection };
  }, [embeddedInModal, scheduleModalApiCollection]);

  const handleScheduleModalCollectionComplete = useCallback(
    (apiConfig) => {
      setScheduleModalApiCollection(apiConfig);
      const firstUrl =
        Array.isArray(apiConfig.api_collection_urls) && apiConfig.api_collection_urls.length > 0
          ? String(apiConfig.api_collection_urls[0]).trim()
          : '';
      onPipelineCollectionConfigured?.({
        apiCollectionURL: firstUrl,
        schemaEndpointPath: String(apiConfig.schema_endpoint_path ?? '').trim(),
      });
      setActiveStep('configuration');
    },
    [onPipelineCollectionConfigured]
  );

  // Throttling state - Default to Standard preset
  const [testStrategy, setTestStrategy] = useState('standard'); // 'stealth', 'standard', 'turbo', 'custom'
  const [rateLimit, setRateLimit] = useState(80); // requests per second
  const [concurrency, setConcurrency] = useState(10); // parallel test cases
  const [requestTimeout, setRequestTimeout] = useState(5); // seconds
  const [showHighRateModal, setShowHighRateModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [declarationSubmitted, setDeclarationSubmitted] = useState(false);
  const [declarationModalOpen, setDeclarationModalOpen] = useState(false);
  const [pendingModalFullConfig, setPendingModalFullConfig] = useState(null);
  const [declarationModalLoading, setDeclarationModalLoading] = useState(false);
  const [pipelineThrottleSaving, setPipelineThrottleSaving] = useState(false);

  useLayoutEffect(() => {
    if (!hideScheduleTab) return;
    if (activeStep === 'schedule') {
      setActiveStep('throttling');
    }
  }, [hideScheduleTab, activeStep]);

  useLayoutEffect(() => {
    if (!embeddedInModal) return;
    if (!['schedule', 'configuration', 'collection'].includes(activeStep)) return;
    const dialogContent = document.querySelector(
      '.MuiDialog-root.MuiModal-root .MuiDialogContent-root'
    );
    dialogContent?.scrollTo?.({ top: 0, behavior: 'auto' });
  }, [embeddedInModal, activeStep]);

  // Preset configurations
  const throttlingPresets = {
    stealth: { rateLimit: 10, concurrency: 1, requestTimeout: 10 },
    standard: { rateLimit: 80, concurrency: 10, requestTimeout: 5 },
    turbo: { rateLimit: 120, concurrency: 20, requestTimeout: 3 },
  };

  // Get gauge label and description based on rate limit
  const getGaugeInfo = (rate) => {
    if (rate >= 1 && rate <= 29) {
      return {
        label: 'Slow',
        color: 'green',
        description: 'Your test will run slowly and carefully, ideal for production',
      };
    } else if (rate >= 30 && rate <= 89) {
      return {
        label: 'Balanced',
        color: 'blue',
        description: 'Your test will run at a balanced pace with moderate speed',
      };
    } else if (rate >= 90 && rate <= 120) {
      return {
        label: 'Fast',
        color: 'orange',
        description: 'Your test will run fast but may trigger rate-limits',
      };
    } else if (rate >= 121 && rate <= 150) {
      return {
        label: 'Intense',
        color: 'red',
        description: 'Your test will run intensely and will likely trigger rate-limits',
      };
    }
    return {
      label: 'Intense',
      color: 'red',
      description: 'Your test will run intensely and will likely trigger rate-limits',
    };
  };

  // Handle preset selection
  const handlePresetSelect = (preset) => {
    if (preset === 'custom') {
      setTestStrategy('custom');
      return;
    }
    const presetValues = throttlingPresets[preset];
    if (presetValues) {
      setTestStrategy(preset);
      setRateLimit(presetValues.rateLimit);
      setConcurrency(presetValues.concurrency);
      setRequestTimeout(presetValues.requestTimeout);
    }
  };

  const handleRateLimitChange = (newValue) => {
    if (newValue < 10) return;
    if (testStrategy !== 'custom') setTestStrategy('custom');
    setRateLimit(newValue);
  };

  const handleConcurrencyChange = (newValue) => {
    if (newValue < 1) return;
    if (testStrategy !== 'custom') setTestStrategy('custom');
    setConcurrency(newValue);
  };

  const handleTimeoutChange = (newValue) => {
    if (newValue < 1) return;
    if (testStrategy !== 'custom') setTestStrategy('custom');
    setRequestTimeout(newValue);
  };

  const shouldFetchOpenVpnProfiles = isInternalAsset || networkType === 'internal';

  // Fetch VPN profiles only when internal / VPN-backed (external API tests do not need OpenVPN list).
  const { data: vpnData = {} } = useIntegrationDetails(ProviderType.OPENVPN, {
    enabled: shouldFetchOpenVpnProfiles,
  });
  const availableVpnProfiles = useMemo(() => {
    if (Array.isArray(vpnData)) {
      return vpnData;
    }
    if (vpnData?.IntegrationData?.details && Array.isArray(vpnData.IntegrationData.details)) {
      return vpnData.IntegrationData.details;
    }
    if (vpnData?.IntegrationDetails && Array.isArray(vpnData.IntegrationDetails)) {
      return vpnData.IntegrationDetails;
    }
    if (vpnData?.data && Array.isArray(vpnData.data)) {
      return vpnData.data;
    }
    if (vpnData?.profiles && Array.isArray(vpnData.profiles)) {
      return vpnData.profiles;
    }
    return [];
  }, [vpnData]);

  // Check if form is valid for submission
  const isFormValid = useMemo(() => {
    // Check VPN requirement for internal mode
    const isVpnRequired = networkType === 'internal' && !(selectedVpnProfile && selectedVpnProfile.id);
    if (isVpnRequired) {
      return false;
    }

    // Get current form values - use formValues for latest state
    const currentCookieAuth = ((formValues?.cookieAuth || cookieAuth) || '').trim();
    // Get headers from formValues to ensure we have the latest typed values
    const currentCustomHeaders = Array.isArray(formValues?.customHeaders)
      ? formValues.customHeaders
      : [];
    
    // Check if we have at least one valid authentication method (Cookie OR Header)
    const hasCookie = showAuthFields && currentCookieAuth.length > 0;
    const hasValidHeader = showAuthFields && Array.isArray(currentCustomHeaders) && currentCustomHeaders.some(
      (header) => {
        const headerName = (header?.headerName || '').trim();
        const headerValue = (header?.headerValue || '').trim();
        return headerName.length > 0 && headerValue.length > 0;
      }
    );
    const hasValidAuth = hasCookie || hasValidHeader;

    // If auth fields are shown, either cookie OR headers are required
    if (showAuthFields && !hasValidAuth) {
      return false;
    }

    // Filter errors based on form state
    // Always ignore auth-related errors since we handle auth validation separately above
    const relevantErrors = { ...errors };
    delete relevantErrors.cookieAuth;
    Object.keys(relevantErrors).forEach(key => {
      if (key.startsWith('customHeaders')) {
        delete relevantErrors[key];
      }
    });

    // Check for remaining errors (non-auth related)
    if (Object.keys(relevantErrors).length > 0) {
      return false;
    }

    return true;
  }, [errors, showAuthFields, cookieAuth, formValues, networkType, selectedVpnProfile]);

  // Submission state
  const [submittedData, setSubmittedData] = useState(submittedDataProp || null);

  /** ROAR-4387: edit prefill can attach `schedule` after first paint; keep local `submittedData` in sync so ScheduleTest mounts with real `initialValues`. */
  useLayoutEffect(() => {
    if (!embeddedInModal) return;
    const sch = submittedDataProp?.schedule;
    if (!sch || typeof sch !== 'object') return;
    setSubmittedData((prev) => {
      const base = prev && typeof prev === 'object' ? prev : {};
      const prevSch = base.schedule;
      const prevMissing =
        !prevSch ||
        typeof prevSch !== 'object' ||
        (!String(prevSch.scheduleName || '').trim() &&
          (!prevSch.startDate || String(prevSch.startDate).trim() === ''));
      if (!prevMissing) return prev;
      const prop = submittedDataProp && typeof submittedDataProp === 'object' ? submittedDataProp : {};
      return { ...base, ...prop, schedule: sch };
    });
  }, [embeddedInModal, submittedDataProp]);

  const apiThrottleLockedByConfig =
    !!submittedData?.config_settings && activeStep !== 'throttling';
  const [, setFormVisible] = useState(!isReadOnly);
  const [expanded, setExpanded] = useState(() =>
    Boolean(
      readOnlyDefaultExpanded &&
        (legacyIsReadOnly || (msg && String(msg.status).toLowerCase() === 'completed'))
    )
  );
  const hasJustSubmitted = useRef(false);
  const hasInitializedFromLatestSession = useRef(false);
  const hasSetVpnProfileFromLatestSession = useRef(false);
  const hasSwitchedToThrottling = useRef(false);
  const hasRestoredThrottling = useRef(false);
  const lastAppliedTestConfigDetailsRef = useRef(null);

  const finalTargetURL = useMemo(
    () =>
      getCanonicalGenptTargetUrl({
        targetURLProp: targetURL,
        msg,
        sessionDetails,
        messages: chatMessages,
      }),
    [targetURL, msg, sessionDetails, chatMessages]
  );

  const buildConfigSettingsFromSliders = useCallback(
    () => ({
      vulnerability: {
        rate_limit: rateLimit,
        concurrency: concurrency,
        timeout: requestTimeout,
        retries: 2,
      },
      validation: {
        timeout: requestTimeout,
        retries: 2,
      },
      /** Required by `persistPipelineGenptFromScheduleWizard` / parity with `ThrottlingSetup.buildConfigSettings`. */
      strategy: testStrategy,
    }),
    [rateLimit, concurrency, requestTimeout, testStrategy]
  );


  const submitDeclaration = async () => {
    const configSettings = buildConfigSettingsFromSliders();
    const payload = {
      ...submittedData,
      config_settings: configSettings,
    };

    if (suppressDeclarationEvent && onConfigCompleted && embeddedInModal) {
      setPendingModalFullConfig(payload);
      setDeclarationModalOpen(true);
      return;
    }

    if (suppressDeclarationEvent && onConfigCompleted) {
      try {
        await onConfigCompleted(payload);
      } catch {
        /* Schedule modal surfaces error popup; keep form editable */
      }
      return;
    }

    setFormVisible(false);
    setContinueIsLoading(false);
    setDeclarationSubmitted(true);

    const declarationMessage = {
      type: MESSAGE_TYPES.DECLARATION_WIDGET,
      from: MESSAGE_SOURCES.SYSTEM,
      text: getMessageText({ type: MESSAGE_TYPES.DECLARATION_WIDGET }, finalTargetURL),
      timestamp: Date.now(),
      widget: {
        type: MESSAGE_TYPES.DECLARATION_WIDGET,
      },
      metadata: {
        testConfig: payload,
        originalMessageType: msg?.type || null,
      },
    };

    if (!suppressDeclarationEvent) {
      window.dispatchEvent(
        new CustomEvent('genpt:addMessage', {
          detail: declarationMessage,
        })
      );
    }

    if (onConfigCompleted) {
      onConfigCompleted(payload);
    }
  };

  const handleContinueToDeclaration = () => {
    if (declarationSubmitted || !submittedData) return;
    if (testStrategy === 'turbo' || rateLimit > 120) {
      setShowHighRateModal(true);
      setPendingAction(() => () => submitDeclaration());
      return;
    }
    submitDeclaration();
  };

  const goToScheduleStep = useCallback(() => {
    setThrottlingConfigForSchedule(buildConfigSettingsFromSliders());
    setActiveStep('schedule');
  }, [buildConfigSettingsFromSliders]);

  const handleStartAndScheduleClick = () => {
    if (declarationSubmitted || !submittedData || pipelineThrottleSaving) return;

    const runAfterThrottleConfirm = () => {
      if (hideScheduleTab) {
        if (typeof onPipelineThrottleSave === 'function') {
          const configSettings = buildConfigSettingsFromSliders();
          const fullConfig = { ...submittedData, config_settings: configSettings };
          setPipelineThrottleSaving(true);
          (async () => {
            try {
              await onPipelineThrottleSave(fullConfig);
              setFormVisible(false);
              setContinueIsLoading(false);
              setDeclarationSubmitted(true);
            } catch {
              /* Caller surfaces errors (e.g. AlertContext); keep form editable */
            } finally {
              setPipelineThrottleSaving(false);
            }
          })();
        }
        return;
      }
      goToScheduleStep();
    };

    if (testStrategy === 'turbo' || rateLimit > 120) {
      setShowHighRateModal(true);
      setPendingAction(() => runAfterThrottleConfirm);
      return;
    }
    runAfterThrottleConfirm();
  };

  const handleScheduleSubmit = async (scheduleData) => {
    if (declarationSubmitted || !submittedData) return;

    /** Always read sliders here: stepper can jump to Schedule without re-running `goToScheduleStep`, and
     * `throttlingConfigForSchedule` must not override with a stale snapshot after throttling edits. */
    const configSettings = buildConfigSettingsFromSliders();

    const fullConfig = {
      ...submittedData,
      config_settings: configSettings,
      schedule: scheduleData,
    };
    if (embeddedInModal && scheduleModalApiCollection) {
      const coll = normalizeScheduleModalCollectionBlob(scheduleModalApiCollection);
      if (coll) {
        delete fullConfig.api_collection_urls;
        delete fullConfig.api_files;
        delete fullConfig.schema_endpoint_url;
        Object.assign(fullConfig, coll);
      }
    }

    if (suppressDeclarationEvent && onConfigCompleted && embeddedInModal) {
      setPendingModalFullConfig(fullConfig);
      setDeclarationModalOpen(true);
      return;
    }

    if (suppressDeclarationEvent && onConfigCompleted) {
      try {
        await onConfigCompleted(fullConfig);
      } catch {
        /* Schedule modal surfaces error popup; keep form editable */
      }
      return;
    }

    setFormVisible(false);
    setContinueIsLoading(false);
    setDeclarationSubmitted(true);

    const declarationMessage = {
      type: MESSAGE_TYPES.DECLARATION_WIDGET,
      from: MESSAGE_SOURCES.SYSTEM,
      text: getMessageText({ type: MESSAGE_TYPES.DECLARATION_WIDGET }, finalTargetURL),
      timestamp: Date.now(),
      widget: {
        type: MESSAGE_TYPES.DECLARATION_WIDGET,
      },
      metadata: {
        testConfig: fullConfig,
        originalMessageType: msg?.type || null,
      },
    };

    if (!suppressDeclarationEvent) {
      window.dispatchEvent(
        new CustomEvent('genpt:addMessage', {
          detail: declarationMessage,
        })
      );
    }

    if (onConfigCompleted) {
      onConfigCompleted(fullConfig);
    }
  };

  const handleDeclarationModalConfirm = async () => {
    if (!pendingModalFullConfig || !onConfigCompleted) return;
    setDeclarationModalLoading(true);
    try {
      await onConfigCompleted(pendingModalFullConfig);
      setFormVisible(false);
      setContinueIsLoading(false);
      setDeclarationSubmitted(true);
      setDeclarationModalOpen(false);
      setPendingModalFullConfig(null);
    } catch {
      /* ScheduleGenPTModal surfaces error popup; keep declaration open for retry */
    } finally {
      setDeclarationModalLoading(false);
    }
  };

  const handleDeclarationModalSetOpen = (next) => {
    if (declarationModalLoading) return;
    setDeclarationModalOpen(next);
    if (!next) setPendingModalFullConfig(null);
  };

  /** API test setup: show 3rd step (Schedule) whenever the form is editable (not pipeline configure-only). */
  const showApiScheduleStep = !isReadOnly && !declarationSubmitted && !hideScheduleTab;
  /** ROAR-4364: Schedule modal uses distinct titled cards per step (not one shared panel). */
  const scheduleModalSeparatedSteps = embeddedInModal && showApiScheduleStep;
  /** After API collection is done: unified “Test setup” card (mockup: 3-step stepper + form inside one widget). */
  const scheduleModalPostCollection =
    scheduleModalSeparatedSteps && activeStep !== 'collection';
  const isApiThrottlingDone = !!throttlingConfigForSchedule;

  const hasSubmittedConfigSettings = Boolean(submittedData?.config_settings);

  // When isReadOnly becomes true, collapse inline chat widget (not Activities view-details modal).
  useEffect(() => {
    if (isReadOnly) {
      setFormVisible(false);
      if (readOnlyAlwaysOpen) {
        setExpanded(true);
      } else {
        setExpanded(false);
      }
    }
  }, [isReadOnly, readOnlyAlwaysOpen]);

  // When expanded after submission, jump to throttling (chat read-only); Activities modal stays on Configuration first.
  useEffect(() => {
    if (readOnlyAlwaysOpen) return;
    if (expanded && hasSubmittedConfigSettings && !hasSwitchedToThrottling.current) {
      setActiveStep('throttling');
      hasSwitchedToThrottling.current = true;
    }
    if (!expanded) {
      hasSwitchedToThrottling.current = false;
    }
  }, [expanded, hasSubmittedConfigSettings, readOnlyAlwaysOpen]);

  // Restore throttling values from previous configuration when switching to throttling step
  useEffect(() => {
    if (activeStep === 'throttling' && !hasRestoredThrottling.current) {
      const configSettings = msg?.metadata?.test_config_details?.config_settings ||
                            msg?.metadata?.latest_session?.test_config_settings ||
                            msg?.metadata?.latest_session?.config_settings ||
                            submittedData?.config_settings;

      const previousThrottling = configSettings ? null : (
        msg?.metadata?.test_config_details?.throttling ||
        msg?.metadata?.latest_session?.throttling ||
        submittedData?.throttling
      );

      if (configSettings?.vulnerability) {
        const vuln = configSettings.vulnerability;
        const rateLimitValue = vuln.rate_limit || 80;
        const concurrencyValue = vuln.concurrency || 10;
        const timeoutValue = vuln.timeout || 5;
        /**
         * Trust `configSettings.strategy` first — it's the user-chosen preset name persisted
         * by `ThrottlingSetup.buildConfigSettings`. Only fall back to numeric inference for
         * older rows that don't carry `strategy`, otherwise any BE drift in the persisted
         * numbers (string/float coercion, server-side clamps) would downgrade Standard / Stealth
         * / Turbo to "Custom" on read-back.
         */
        const rawStrategy = String(configSettings.strategy ?? '').trim().toLowerCase();
        let matchedPreset = null;
        if (rawStrategy && throttlingPresets[rawStrategy]) {
          matchedPreset = rawStrategy;
        } else if (rawStrategy !== 'custom') {
          for (const [presetName, preset] of Object.entries(throttlingPresets)) {
            if (rateLimitValue === preset.rateLimit &&
                concurrencyValue === preset.concurrency &&
                timeoutValue === preset.requestTimeout) {
              matchedPreset = presetName;
              break;
            }
          }
        }
        if (matchedPreset) {
          setTestStrategy(matchedPreset);
          setRateLimit(throttlingPresets[matchedPreset].rateLimit);
          setConcurrency(throttlingPresets[matchedPreset].concurrency);
          setRequestTimeout(throttlingPresets[matchedPreset].requestTimeout);
        } else {
          setTestStrategy('custom');
          setRateLimit(rateLimitValue);
          setConcurrency(concurrencyValue);
          setRequestTimeout(timeoutValue);
        }
        hasRestoredThrottling.current = true;
      } else if (previousThrottling?.testStrategy && throttlingPresets[previousThrottling.testStrategy]) {
        const preset = throttlingPresets[previousThrottling.testStrategy];
        const matchesPreset =
          previousThrottling.rateLimit === preset.rateLimit &&
          previousThrottling.concurrency === preset.concurrency &&
          previousThrottling.requestTimeout === preset.requestTimeout;
        if (matchesPreset) {
          setTestStrategy(previousThrottling.testStrategy);
          setRateLimit(preset.rateLimit);
          setConcurrency(preset.concurrency);
          setRequestTimeout(preset.requestTimeout);
        } else {
          setTestStrategy('custom');
          setRateLimit(previousThrottling.rateLimit || 80);
          setConcurrency(previousThrottling.concurrency || 10);
          setRequestTimeout(previousThrottling.requestTimeout || 5);
        }
        hasRestoredThrottling.current = true;
      }
    } else if (activeStep === 'configuration') {
      hasRestoredThrottling.current = false;
    }
    // Narrow deps: full metadata/throttling objects change identity often; refs gate one-time restore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, !!msg?.metadata?.test_config_details?.config_settings, !!msg?.metadata?.latest_session?.test_config_settings, !!submittedData?.config_settings, !!msg?.metadata?.test_config_details?.throttling, !!msg?.metadata?.latest_session?.throttling, !!submittedData?.throttling]);

  // Load data from metadata?.latest_session if present (takes precedence)
  useEffect(() => {
    const latestSession = msg?.metadata?.latest_session;
    if (latestSession && !isReadOnly && !hasInitializedFromLatestSession.current) {
      // Map latest_session data to form fields
      // Set network type based on vpn_profile or network_access
      if (latestSession.vpn_profile) {
        setNetworkType('internal');
        // Handle different VPN profile structures
        const vpnProfile = latestSession.vpn_profile;
        if (typeof vpnProfile === 'object' && vpnProfile.profile_id) {
          // If it's an object with profile_id (from backend), store ID temporarily - will be matched when VPN profiles load
          setSelectedVpnProfile({ id: vpnProfile.profile_id });
          hasSetVpnProfileFromLatestSession.current = true;
        } else if (typeof vpnProfile === 'object' && vpnProfile.id) {
          // If it's an object with id, use it directly
          setSelectedVpnProfile(vpnProfile);
          hasSetVpnProfileFromLatestSession.current = true;
        } else {
          // If it's a string/number ID, store temporarily
          setSelectedVpnProfile({ id: vpnProfile });
          hasSetVpnProfileFromLatestSession.current = true;
        }
      } else {
        if (latestSession.network_access) {
          setNetworkType(latestSession.network_access);
        } else if (!isInternalAsset) {
          setNetworkType('external');
        } else {
          setNetworkType('internal');
        }
      }
      
      // Set authentication fields
      if (latestSession.authenticated_test !== undefined) {
        setShowAuthFields(latestSession.authenticated_test);
      }
      
      // Prefill from api_config if available
      const apiConfig = latestSession.api_config;
      if (apiConfig) {
        // Prefill cookie authentication from api_config.cookies
        if (apiConfig.cookies) {
          setValue('cookieAuth', apiConfig.cookies);
        } else if (latestSession.cookie_authentication) {
          // Fallback to cookie_authentication if api_config.cookies is not available
          setValue('cookieAuth', latestSession.cookie_authentication);
        }

        // Prefill custom headers from api_config.header (only header name, not value)
        if (Array.isArray(apiConfig.header) && apiConfig.header.length) {
          stableReplaceHeaders(
            apiConfig.header.map((header) => ({
              headerName: header?.headerName || header?.header_name || '',
              headerValue: '',
            }))
          );
        } else if (Array.isArray(latestSession.custom_headers) && latestSession.custom_headers.length) {
          // Fallback to custom_headers if api_config.header is not available (only header name, not value)
          stableReplaceHeaders(
            latestSession.custom_headers.map((header) => ({
              headerName: header?.header_name || header?.headerName || '',
              headerValue: '',
            }))
          );
        } else {
          stableReplaceHeaders([{ headerName: '', headerValue: '' }]);
        }
      } else {
        // Fallback to old structure if api_config is not available
        if (latestSession.cookie_authentication) {
          setValue('cookieAuth', latestSession.cookie_authentication);
        }

        if (Array.isArray(latestSession.custom_headers) && latestSession.custom_headers.length) {
          stableReplaceHeaders(
            latestSession.custom_headers.map((header) => ({
              headerName: header?.header_name || header?.headerName || '',
              headerValue: '',
            }))
          );
        } else {
          stableReplaceHeaders([{ headerName: '', headerValue: '' }]);
        }
      }
      
      // Mark as initialized to prevent re-prefilling if user makes changes
      hasInitializedFromLatestSession.current = true;
    } else if (latestSession && isReadOnly && !hasJustSubmitted.current) {
      // Only set submittedData for read-only display (when form is completed)
      // Priority: test_config_details > submittedData (just submitted) > latest_session
      // Don't overwrite submittedData if it was just set from form submission
      const testConfigDetails = msg?.metadata?.test_config_details;
      if (!testConfigDetails) {
        // Only set from latest_session if submittedData doesn't exist yet
        // This prevents overwriting submittedData that was just set from form submission
        if (!submittedData) {
          const apiConfig = latestSession.api_config;
          const vpnProfile = latestSession.vpn_profile;
          // Extract VPN profile name if it's an object
          const vpnProfileName = vpnProfile?.profile_name || 
                                (typeof vpnProfile === 'object' && vpnProfile?.providerData?.profileName) ||
                                null;
          const vpnProfileId = typeof vpnProfile === 'object' 
            ? (vpnProfile?.profile_id || vpnProfile?.id)
            : vpnProfile;
          
          const mappedData = {
            networkType: latestSession.vpn_profile ? 'internal' : (latestSession.network_access || 'external'),
            authenticated_test: latestSession.authenticated_test,
            selectedVpnProfile: vpnProfileId, // Store ID only
            selectedVpnProfileName: vpnProfileName, // Store name separately
            showAuthFields: latestSession.authenticated_test,
            cookieAuthentication: apiConfig?.cookies || latestSession.cookie_authentication || '',
            customHeaders: apiConfig?.header || latestSession.custom_headers || [],
          };
          setSubmittedData(mappedData);
        }
      }
    }
  }, [
    msg?.metadata?.latest_session,
    msg?.metadata?.test_config_details,
    submittedData,
    setValue,
    mergedValidateTargetData?.crm_type,
    isReadOnly,
    isInternalAsset,
    stableReplaceHeaders,
  ]);

  // Populate form with submitted data if provided (for read-only mode)
  useEffect(() => {
    // Priority for read-only mode: test_config_details > submittedData (just submitted) > latest_session > others
    const latestSession = msg?.metadata?.latest_session;
    const testConfigDetails = msg?.metadata?.test_config_details;
    
    // Transform test_config_details to match expected dataSource format
    if (!testConfigDetails) {
      lastAppliedTestConfigDetailsRef.current = null;
    }
    let transformedTestConfigDetails = null;
    if (testConfigDetails) {
      transformedTestConfigDetails = {
        ...(testConfigDetails.authentication_type !== undefined && {
          showAuthFields: testConfigDetails.authentication_type === 'authenticated'
        }),
        // VPN profile: use profile_id and profile_name to create proper object
        ...(testConfigDetails.vpn_profile?.profile_id && {
          selectedVpnProfile: {
            id: testConfigDetails.vpn_profile.profile_id,
            providerData: {
              profileName: testConfigDetails.vpn_profile.profile_name
            }
          }
        }),
        // Set networkType: if VPN profile exists, it's internal; otherwise check network_access or default to external
        ...(testConfigDetails.vpn_profile 
          ? { networkType: 'internal' }
          : (testConfigDetails.network_access 
              ? { networkType: testConfigDetails.network_access }
              : (testConfigDetails.networkType 
                  ? { networkType: testConfigDetails.networkType }
                  : { networkType: 'external' }))),
        ...(testConfigDetails.cookie_authentication !== undefined && {
          cookieAuthentication: testConfigDetails.cookie_authentication
        }),
        ...(testConfigDetails.custom_headers && Array.isArray(testConfigDetails.custom_headers) && {
          customHeaders: testConfigDetails.custom_headers.map(header => ({
            headerName: header?.header_name || header?.headerName || '',
            headerValue: header?.header_value || header?.headerValue || ''
          }))
        }),
        ...(testConfigDetails.header && Array.isArray(testConfigDetails.header) && {
          customHeaders: testConfigDetails.header.map(header => ({
            headerName: header?.header_name || header?.headerName || '',
            headerValue: header?.header_value || header?.headerValue || ''
          }))
        }),
      };
    }
    
    /**
     * ROAR-4423: in read-only mode prefer the already-normalized camelCase sources only.
     * The earlier effect (latest_session → read-only branch) writes a camelCase `mappedData`
     * into `submittedData`; including raw snake_case `latestSession` here as a same-pass
     * fallback used to overwrite that with snake_case, which silently broke the auth-fields
     * prefill (cookieAuth / customHeaders / showAuthFields) in Activities → View Setup Details.
     * Skip `latestSession` here and let the earlier effect own that conversion so this effect
     * picks up `submittedData` on the next render.
     */
    const dataSource = isReadOnly
      ? (transformedTestConfigDetails || submittedData || testConfiguration || submittedDataProp)
      : (latestSession ? null : (testConfiguration || submittedDataProp || submittedData));
    
    if (dataSource && isReadOnly) {
      // Never overwrite data that was just submitted from the form
      if (hasJustSubmitted.current) {
        // Still populate form state from the submitted data for UI consistency
      } else if (transformedTestConfigDetails) {
        if (msg?.metadata?.test_config_details !== lastAppliedTestConfigDetailsRef.current) {
          lastAppliedTestConfigDetailsRef.current = msg?.metadata?.test_config_details;
          setSubmittedData(transformedTestConfigDetails);
        }
      } else if (!submittedData) {
        setSubmittedData(dataSource);
      }
      // Populate form state from submitted data
      if (!isInternalAsset && dataSource.networkType) {
        setNetworkType(dataSource.networkType);
      } else if (isInternalAsset) {
        setNetworkType('internal');
      }
      
      // For non-WordPress tests, restore showAuthFields
      if (dataSource.showAuthFields !== undefined) {
        setShowAuthFields(dataSource.showAuthFields);
      } else {
        const hasCookie = !!dataSource.cookieAuthentication;
        const hasHeaders =
          Array.isArray(dataSource.customHeaders) &&
          dataSource.customHeaders.some(
            (header) => header?.headerName?.trim() && header?.headerValue?.trim()
          );
        setShowAuthFields(hasCookie || hasHeaders);
      }
      
      // Populate form values if available
      if (dataSource.cookieAuthentication !== undefined && dataSource.cookieAuthentication !== null && dataSource.cookieAuthentication !== '') {
        setValue('cookieAuth', dataSource.cookieAuthentication);
      }
      if (Array.isArray(dataSource.customHeaders)) {
        if (dataSource.customHeaders.length) {
          stableReplaceHeaders(
            dataSource.customHeaders.map((header) => ({
              headerName: header?.headerName || header?.header_name || '',
              headerValue: header?.headerValue || header?.header_value || '',
            }))
          );
        } else {
          stableReplaceHeaders([{ headerName: '', headerValue: '' }]);
        }
      }
      // VPN profile will be restored after VPN profiles are loaded (see useEffect below)
      if (dataSource.selectedVpnProfile) {
        if (typeof dataSource.selectedVpnProfile === 'string' || typeof dataSource.selectedVpnProfile === 'number') {
          // If it's just an ID, try to find the matching profile from available profiles
          const matchedProfile = availableVpnProfiles.find(profile => 
            String(profile.id) === String(dataSource.selectedVpnProfile)
          );
          if (matchedProfile) {
            setSelectedVpnProfile(matchedProfile);
          } else {
            // If not found, create a minimal object with just ID
            setSelectedVpnProfile({ id: dataSource.selectedVpnProfile });
          }
        } else if (dataSource.selectedVpnProfile && typeof dataSource.selectedVpnProfile === 'object') {
          // If it's already an object (with providerData.profileName), use it directly
          setSelectedVpnProfile(dataSource.selectedVpnProfile);
        }
      }
    }
  }, [
    testConfiguration,
    submittedDataProp,
    submittedData,
    isReadOnly,
    setValue,
    mergedValidateTargetData?.crm_type,
    isInternalAsset,
    stableReplaceHeaders,
    msg?.metadata?.latest_session,
    msg?.metadata?.test_config_details,
    availableVpnProfiles,
  ]);

  // Restore form state when submittedData exists (after form submission)
  useEffect(() => {
    if (submittedData && !isReadOnly) {
      const dataSource = submittedData;
      // Populate form state from submitted data
      if (!isInternalAsset && dataSource.networkType) {
        setNetworkType(dataSource.networkType);
      } else if (isInternalAsset) {
        setNetworkType('internal');
      }
      
      // For non-WordPress tests, restore showAuthFields
      if (dataSource.showAuthFields !== undefined) {
        setShowAuthFields(dataSource.showAuthFields);
      } else {
        const hasCookie = !!dataSource.cookieAuthentication;
        const hasHeaders =
          Array.isArray(dataSource.customHeaders) &&
          dataSource.customHeaders.some(
            (header) => header?.headerName?.trim() && header?.headerValue?.trim()
          );
        setShowAuthFields(hasCookie || hasHeaders);
      }
    
      // Populate form values if available
      if (dataSource.cookieAuthentication !== undefined) {
        setValue('cookieAuth', dataSource.cookieAuthentication || '');
      }
      if (Array.isArray(dataSource.customHeaders)) {
        if (dataSource.customHeaders.length) {
          stableReplaceHeaders(
            dataSource.customHeaders.map((header) => ({
              headerName: header?.headerName || header?.header_name || '',
              headerValue: header?.headerValue || header?.header_value || '',
            }))
          );
        } else {
          stableReplaceHeaders([{ headerName: '', headerValue: '' }]);
        }
      }
      // VPN profile will be restored after VPN profiles are loaded (see useEffect below)
      if (dataSource.selectedVpnProfile) {
        if (typeof dataSource.selectedVpnProfile === 'string' || typeof dataSource.selectedVpnProfile === 'number') {
          // Store VPN profile ID temporarily - will be matched when VPN profiles load
          setSelectedVpnProfile({ id: dataSource.selectedVpnProfile });
        } else {
          setSelectedVpnProfile(dataSource.selectedVpnProfile);
        }
      }
    }
  }, [submittedData, isReadOnly, setValue, mergedValidateTargetData?.crm_type, isInternalAsset, stableReplaceHeaders, msg?.metadata?.test_config_details, msg?.metadata?.latest_session, testConfiguration, submittedDataProp]);

  // Repopulate form values when expanded in read-only mode
  useEffect(() => {
    if (isReadOnly && expanded && submittedData) {
      // Repopulate form values from submittedData when form is expanded
      if (submittedData.cookieAuthentication !== undefined && submittedData.cookieAuthentication !== null && submittedData.cookieAuthentication !== '') {
        setValue('cookieAuth', submittedData.cookieAuthentication);
      }
      if (Array.isArray(submittedData.customHeaders)) {
        if (submittedData.customHeaders.length) {
          stableReplaceHeaders(
            submittedData.customHeaders.map((header) => ({
              headerName: header?.headerName || header?.header_name || '',
              headerValue: header?.headerValue || header?.header_value || '',
            }))
          );
        } else {
          stableReplaceHeaders([{ headerName: '', headerValue: '' }]);
        }
      }
      // Set network type
      if (submittedData.networkType) {
        setNetworkType(submittedData.networkType);
      }
      // Set showAuthFields
      if (submittedData.showAuthFields !== undefined) {
        setShowAuthFields(submittedData.showAuthFields);
      }
      // Set VPN profile if available
      if (submittedData.selectedVpnProfile) {
        if (typeof submittedData.selectedVpnProfile === 'string' || typeof submittedData.selectedVpnProfile === 'number') {
          // If it's just an ID, try to find the matching profile from available profiles
          const matchedProfile = availableVpnProfiles.find(profile => 
            String(profile.id) === String(submittedData.selectedVpnProfile)
          );
          if (matchedProfile) {
            setSelectedVpnProfile(matchedProfile);
          } else {
            // If not found, create a minimal object with just ID
            setSelectedVpnProfile({ id: submittedData.selectedVpnProfile });
          }
        } else if (submittedData.selectedVpnProfile && typeof submittedData.selectedVpnProfile === 'object') {
          // If it's already an object (with providerData.profileName), use it directly
          setSelectedVpnProfile(submittedData.selectedVpnProfile);
        } 
      }
    }
  }, [isReadOnly, expanded, submittedData, setValue, stableReplaceHeaders, availableVpnProfiles]);

  // Restore VPN profile from test_config_details/submittedData after VPN profiles API loads
  useEffect(() => {
    const testConfigDetails = msg?.metadata?.test_config_details;
    const latestSession = msg?.metadata?.latest_session;
    const vpnProfileIdFromTestConfig = testConfigDetails?.vpn_profile?.profile_id || testConfigDetails?.vpn_profile;
    const vpnProfileNameFromTestConfig = testConfigDetails?.vpn_profile?.profile_name;
    const vpnProfileIdFromLatestSession = latestSession?.vpn_profile?.profile_id || latestSession?.vpn_profile;
    
    // Only restore from latest_session via submittedData if we haven't already set it (allow user to change it)
    // But always restore from test_config_details (read-only mode)
    const shouldRestoreFromLatestSession = vpnProfileIdFromLatestSession && 
                                          submittedData?.selectedVpnProfile && 
                                          String(submittedData.selectedVpnProfile) === String(vpnProfileIdFromLatestSession) &&
                                          !hasSetVpnProfileFromLatestSession.current;
    
    // Priority: test_config_details > latest_session (only if not already set) > submittedData > testConfiguration > submittedDataProp
    const vpnProfileId = vpnProfileIdFromTestConfig || 
                        (shouldRestoreFromLatestSession ? vpnProfileIdFromLatestSession : null) ||
                        (hasSetVpnProfileFromLatestSession.current ? null : submittedData?.selectedVpnProfile) ||
                        testConfiguration?.selectedVpnProfile || 
                        submittedDataProp?.selectedVpnProfile;
    
    if (vpnProfileId && availableVpnProfiles.length > 0) {
      const currentVpnId = selectedVpnProfile?.id;
      const needsRestore = !currentVpnId || String(currentVpnId) !== String(vpnProfileId);
      
      if (needsRestore) {
        if (typeof vpnProfileId === 'string' || typeof vpnProfileId === 'number') {
          // It's an ID, find the matching profile object
          const matchedProfile = availableVpnProfiles.find(profile => 
            String(profile.id) === String(vpnProfileId)
          );
          if (matchedProfile) {
            setSelectedVpnProfile(matchedProfile);
            // Mark as set if it came from latest_session
            if (shouldRestoreFromLatestSession && String(matchedProfile.id) === String(vpnProfileIdFromLatestSession)) {
              hasSetVpnProfileFromLatestSession.current = true;
            }
            // Update submittedData with VPN profile name if it exists
            if (submittedData && matchedProfile?.providerData?.profileName) {
              setSubmittedData(prev => ({
                ...prev,
                selectedVpnProfileName: matchedProfile.providerData.profileName
              }));
            }
          } else if (vpnProfileNameFromTestConfig) {
            // If profile not found in available profiles but we have the name, create object with name
            setSelectedVpnProfile({
              id: vpnProfileId,
              providerData: {
                profileName: vpnProfileNameFromTestConfig
              }
            });
          }
        } else if (vpnProfileId && typeof vpnProfileId === 'object') {
          // It's already an object
          setSelectedVpnProfile(vpnProfileId);
          // Mark as set if it came from latest_session
          if (shouldRestoreFromLatestSession && String(vpnProfileId.id) === String(vpnProfileIdFromLatestSession)) {
            hasSetVpnProfileFromLatestSession.current = true;
          }
          // Update submittedData with VPN profile name if it exists
          if (submittedData && vpnProfileId?.providerData?.profileName) {
            setSubmittedData(prev => ({
              ...prev,
              selectedVpnProfileName: vpnProfileId.providerData.profileName
            }));
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msg?.metadata?.test_config_details?.vpn_profile?.profile_id, msg?.metadata?.test_config_details?.vpn_profile?.profile_name, msg?.metadata?.latest_session?.vpn_profile?.profile_id, submittedData?.selectedVpnProfile, testConfiguration?.selectedVpnProfile, submittedDataProp?.selectedVpnProfile, availableVpnProfiles]);

  const showWaitingForURL = msg && !finalTargetURL;

  const onSubmit = async (data) => {
    if (Object.keys(errors).length === 0) {
      setContinueIsLoading(true);

      const cookieAuthentication = showAuthFields ? (data?.cookieAuth?.trim() || '') : '';
      const formattedHeaders =
        showAuthFields
          ? (data?.customHeaders || [])
              .filter((header) => header?.headerName?.trim())
              .map((header) => ({
                headerName: header.headerName.trim(),
                headerValue: (header.headerValue || '').trim(),
              }))
          : [];

      const authType = showAuthFields ? 'authenticated' : 'unauthenticated';
      const vpnProfileName = selectedVpnProfile?.providerData?.profileName || selectedVpnProfile?.name || null;

      const collectionFields =
        embeddedInModal && scheduleModalApiCollection
          ? {
              ...(Array.isArray(scheduleModalApiCollection.api_collection_urls) &&
                scheduleModalApiCollection.api_collection_urls.length > 0 && {
                  api_collection_urls: scheduleModalApiCollection.api_collection_urls,
                }),
              ...(Array.isArray(scheduleModalApiCollection.api_files) &&
                scheduleModalApiCollection.api_files.length > 0 && {
                  api_files: scheduleModalApiCollection.api_files,
                }),
              ...(scheduleModalApiCollection.schema_endpoint_url && {
                schema_endpoint_url: scheduleModalApiCollection.schema_endpoint_url,
              }),
            }
          : {};

      let configData = {
        ...collectionFields,
        targetURL: finalTargetURL,
        authType,
        sideFile: null,
        networkType,
        selectedVpnProfile: selectedVpnProfile?.id || null,
        selectedVpnProfileName: vpnProfileName,
        showAuthFields,
        assetId: msg?.metadata?.asset?.id || null,
        cookieAuthentication: null,
        customHeaders: [],
      };

      configData = {
        ...configData,
        cookieAuthentication: cookieAuthentication || null,
        customHeaders: formattedHeaders,
      };

      hasJustSubmitted.current = true;
      // Merge so edit prefill + collection keys (api_*, schedule) survive Configuration submit —
      // TestSetup-style payloads omit them and would otherwise clear GET state before PUT (ROAR-4387).
      setSubmittedData((prev) => {
        const next = {
          ...(prev || {}),
          ...configData,
          ...(prev?.schedule ? { schedule: prev.schedule } : {}),
        };
        if (embeddedInModal && scheduleModalApiCollection) {
          const coll = normalizeScheduleModalCollectionBlob(scheduleModalApiCollection);
          if (coll) {
            delete next.api_collection_urls;
            delete next.api_files;
            delete next.schema_endpoint_url;
            Object.assign(next, coll);
          }
        }
        return next;
      });
      setContinueIsLoading(false);
      // Move to throttling step after configuration is saved; declaration is submitted from throttling section
      setTimeout(() => {
        setActiveStep('throttling');
      }, 100);
    }
  };

  const handleClose = () => {
    setOpen(false);
    reset();
    navigate(`${mainUrl}/genpt/tests/start`);
  };

  const rootChrome = embeddedInModal
    ? scheduleModalSeparatedSteps
      ? 'min-h-[64px] w-full'
      : 'min-h-[64px] w-full rounded-lg border border-gray-200 bg-white p-4'
    : readOnlyUseModalHeader
      ? 'min-h-[64px] w-full rounded-lg border border-gray-200 bg-white px-2.5 py-2 sm:px-3 sm:py-2.5 text-left'
      : 'border border-[#EFEFEF] rounded-xl p-4 bg-white shadow-md transition-all duration-300 min-h-[64px] w-full md:w-[90%]';

  const readOnlyCollectionSessionDetails = useMemo(() => {
    if (!isReadOnly) return null;
    const ac = msg?.metadata?.latest_session?.api_config;
    if (!ac || typeof ac !== 'object') return null;
    const hasMulti =
      (Array.isArray(ac.api_files) && ac.api_files.length > 0) ||
      (Array.isArray(ac.api_collection_urls) && ac.api_collection_urls.length > 0) ||
      (Array.isArray(ac.files) && ac.files.length > 0);
    const hasLegacy = !!(ac.file_key || ac.collection_url || ac.collection_s3_filename);
    if (!hasMulti && !hasLegacy) return null;
    return { api_config: ac };
  }, [isReadOnly, msg?.metadata?.latest_session?.api_config]);

  const scheduleStepSubmitLabel =
    !embeddedInModal || scheduleSubmitConfirmAndSave ? 'Confirm and Save' : 'SCHEDULE TEST';

  /** Schedule modal (post–API collection): Configuration → Throttling → Schedule — same control strip as chat. */
  const renderApiScheduleThreeStepper = () => (
    <div className="relative flex items-start justify-between w-full md:w-[80%] min-w-0 px-2 mx-auto">
      <div
        className="flex flex-col items-center cursor-pointer flex-shrink-0"
        onClick={() => setActiveStep('configuration')}
      >
        <div
          className={`rounded-full flex items-center justify-center text-white font-semibold transition-colors relative z-10 ${
            embeddedInModal ? 'w-7 h-7 text-xs' : 'w-10 h-10 text-sm'
          } ${
            declarationSubmitted || isReadOnly || (submittedData && activeStep !== 'configuration')
              ? ''
              : activeStep === 'configuration'
                ? 'bg-primary-500'
                : 'bg-primary-200'
          }`}
          style={{
            ...(declarationSubmitted || isReadOnly || (submittedData && activeStep !== 'configuration')
              ? { backgroundColor: '#3ADA7A' }
              : {}),
            opacity: 1,
          }}
        >
          1
        </div>
        <div
          className={`font-semibold whitespace-nowrap ${
            embeddedInModal ? 'mt-1.5 text-xs' : 'mt-2 text-sm'
          } ${activeStep === 'configuration' || submittedData ? 'text-black' : 'text-gray-600'}`}
        >
          Configuration
        </div>
      </div>
      <div
        className={`absolute h-0.5 ${embeddedInModal ? 'top-3.5' : 'top-5'}`}
        style={showApiScheduleStep ? { left: '10%', right: '57%' } : { left: '20%', right: '20%' }}
      >
        <div
          className={`h-full w-full transition-colors ${
            declarationSubmitted || isReadOnly || (submittedData && activeStep !== 'configuration')
              ? ''
              : activeStep === 'configuration' || activeStep === 'throttling'
                ? 'bg-primary-500'
                : submittedData
                  ? ''
                  : 'bg-primary-200'
          }`}
          style={
            declarationSubmitted || isReadOnly || (submittedData && activeStep !== 'configuration')
              ? { backgroundColor: '#3ADA7A' }
              : {}
          }
        />
      </div>
      <div
        className="flex flex-col items-center cursor-pointer flex-shrink-0"
        onClick={() => submittedData && setActiveStep('throttling')}
        style={{ cursor: submittedData ? 'pointer' : 'not-allowed' }}
      >
        <div
          className={`rounded-full flex items-center justify-center text-white font-semibold transition-colors relative z-10 ${
            embeddedInModal ? 'w-7 h-7 text-xs' : 'w-10 h-10 text-sm'
          } ${
            (() => {
              const done =
                declarationSubmitted ||
                isReadOnly ||
                (isApiThrottlingDone && activeStep !== 'throttling');
              if (done) return '';
              if (activeStep === 'throttling' && !declarationSubmitted && !isReadOnly) {
                return 'bg-primary-500';
              }
              return 'bg-primary-200';
            })()
          }`}
          style={
            declarationSubmitted || isReadOnly || (isApiThrottlingDone && activeStep !== 'throttling')
              ? { backgroundColor: '#3ADA7A' }
              : {}
          }
        >
          2
        </div>
        <div
          className={`font-semibold whitespace-nowrap ${
            embeddedInModal ? 'mt-1.5 text-xs' : 'mt-2 text-sm'
          } ${
            activeStep === 'throttling' ||
            declarationSubmitted ||
            (isApiThrottlingDone && activeStep !== 'throttling')
              ? 'text-black'
              : 'text-gray-600'
          }`}
        >
          Throttling
        </div>
      </div>
      {showApiScheduleStep && (
        <>
          <div
            className={`absolute h-0.5 ${embeddedInModal ? 'top-3.5' : 'top-5'}`}
            style={{ left: '43%', right: '10%' }}
          >
            <div
              className={`h-full w-full transition-colors ${
                declarationSubmitted || isReadOnly || isApiThrottlingDone ? '' : 'bg-primary-200'
              }`}
              style={
                declarationSubmitted || isReadOnly || isApiThrottlingDone
                  ? { backgroundColor: '#3ADA7A' }
                  : {}
              }
            />
          </div>
          <div
            className="flex flex-col items-center flex-shrink-0"
            onClick={() => isApiThrottlingDone && setActiveStep('schedule')}
            style={{ cursor: isApiThrottlingDone ? 'pointer' : 'not-allowed' }}
          >
            <div
              className={`rounded-full flex items-center justify-center text-white font-semibold transition-colors relative z-10 ${
                embeddedInModal ? 'w-7 h-7 text-xs' : 'w-10 h-10 text-sm'
              } ${
                embeddedInModal && declarationSubmitted
                  ? 'bg-white ring-2 ring-[#3ADA7A] ring-inset'
                  : activeStep === 'schedule' && !declarationSubmitted && !isReadOnly
                    ? 'bg-primary-500'
                    : declarationSubmitted || isReadOnly
                      ? ''
                      : 'bg-primary-200'
              }`}
              style={
                embeddedInModal && declarationSubmitted
                  ? {}
                  : declarationSubmitted || isReadOnly
                    ? { backgroundColor: '#3ADA7A' }
                    : {}
              }
            >
              {embeddedInModal && declarationSubmitted ? (
                <GreenCheck className="!w-5 !h-5 flex-shrink-0" aria-hidden />
              ) : (
                '3'
              )}
            </div>
            <div
              className={`font-semibold whitespace-nowrap ${
                embeddedInModal ? 'mt-1.5 text-xs' : 'mt-2 text-sm'
              } ${activeStep === 'schedule' || declarationSubmitted ? 'text-black' : 'text-gray-600'}`}
            >
              Schedule
            </div>
          </div>
        </>
      )}
    </div>
  );

  return showWaitingForURL ? (
    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
      <p className="text-yellow-800">Waiting for target URL...</p>
    </div>
  ) : (
    <>
    <div className={rootChrome}>
      {isReadOnly ? (
        <>
          {/* Collapsible Header for Read-Only Mode (Activities modal uses Handyman + close, like web TestConfigurationForm). */}
          <div className="flex items-center justify-between w-full mb-2">
            {readOnlyUseModalHeader ? (
              <>
                <div className="flex items-center min-w-0 flex-1 text-lg font-semibold text-gray-800">
                  <HandymanIcon className="mr-2 text-primary-500 shrink-0" aria-hidden />
                  <span className="truncate">API test setup</span>
                </div>
                <div className="flex items-center shrink-0 gap-0.5">
                  {!readOnlyAlwaysOpen && (
                    <button
                      type="button"
                      onClick={() => setExpanded(!expanded)}
                      className="p-2 hover:bg-gray-100 rounded-full transition-all text-gray-600"
                      aria-expanded={expanded}
                      aria-label={expanded ? 'Collapse test setup' : 'Expand test setup'}
                    >
                      {expanded ? (
                        <KeyboardArrowUpIcon sx={{ fontSize: 22 }} />
                      ) : (
                        <KeyboardArrowDownIcon sx={{ fontSize: 22 }} />
                      )}
                    </button>
                  )}
                  {typeof onReadOnlyModalClose === 'function' && (
                    <button
                      type="button"
                      onClick={onReadOnlyModalClose}
                      className="p-1.5 hover:bg-gray-100 rounded-full transition-all text-gray-600"
                      aria-label="Close"
                    >
                      <CloseIcon sx={{ fontSize: 20 }} />
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <GreenCheck />
                  <Typography variant="subtitle1" className="font-normal">
                    Test Setup
                  </Typography>
                </div>
                <button
                  type="button"
                  onClick={() => setExpanded(!expanded)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-all"
                  aria-expanded={expanded}
                  aria-label={expanded ? 'Collapse test setup' : 'Expand test setup'}
                >
                  {expanded ? (
                    <ExpandLessIcon className="text-gray-600" />
                  ) : (
                    <ExpandMoreIcon className="text-gray-600" />
                  )}
                </button>
              </>
            )}
          </div>

          {expanded && (
            <>
              {/* Chat already renders API_COLLECTION as its own message; only inline here in schedule modal (no duplicate thread widget). */}
              {readOnlyCollectionSessionDetails && !readOnlyUseModalHeader && embeddedInModal && (
                <div className="mb-4 rounded-xl border border-[#EFEFEF] bg-white p-3 shadow-sm pointer-events-none opacity-90">
                  <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800">
                    <PublicIcon className="text-primary-500" fontSize="small" aria-hidden />
                    Collection source
                  </div>
                  <APICollectionSourceWidget
                    msg={msg}
                    targetURL={finalTargetURL}
                    chatId={chatId}
                    sessionId={sessionId}
                    sessionDetails={readOnlyCollectionSessionDetails}
                  />
                </div>
              )}
              {/* Stepper - 70% width, centered */}
              <div
                className={
                  embeddedInModal
                    ? 'mt-4 mb-4 flex justify-center px-0 py-2'
                    : 'mt-4 mb-4 bg-gray-50 rounded-lg p-4 flex justify-center'
                }
              >
                <div className="relative flex items-start justify-between w-full md:w-[70%] min-w-0 px-2">
                  <div className="flex flex-col items-center cursor-pointer flex-shrink-0" onClick={() => setActiveStep('configuration')}>
                    <div 
                      className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-white font-semibold transition-colors relative z-10 ${declarationSubmitted || isReadOnly || (submittedData && activeStep !== 'configuration') ? '' : (activeStep === 'configuration' ? 'bg-primary-500' : 'bg-primary-200')}`}
                      style={{ ...(declarationSubmitted || isReadOnly || (submittedData && activeStep !== 'configuration') ? { backgroundColor: '#3ADA7A' } : {}), opacity: 1 }}
                    >1</div>
                    <div
                      className={`mt-2 text-sm font-semibold whitespace-nowrap ${
                        (declarationSubmitted ||
                          isReadOnly ||
                          (submittedData && activeStep !== 'configuration')) &&
                        activeStep !== 'configuration'
                          ? 'text-primary-500 underline decoration-primary-500 underline-offset-2'
                          : activeStep === 'configuration' || submittedData
                            ? 'text-black'
                            : 'text-gray-600'
                      }`}
                    >
                      Configuration
                    </div>
                  </div>
                  {/* Connecting Line */}
                  <div className="absolute top-4 md:top-5 left-10 right-10 h-0.5">
                    <div 
                      className={`h-full w-full transition-colors ${declarationSubmitted || isReadOnly || (submittedData && activeStep !== 'configuration') ? '' : (activeStep === 'configuration' || activeStep === 'throttling') ? 'bg-primary-500' : (submittedData ? '' : 'bg-primary-200')}`}
                      style={declarationSubmitted || isReadOnly || (submittedData && activeStep !== 'configuration') ? { backgroundColor: '#3ADA7A' } : {}}
                    />
                  </div>
                  <div className="flex flex-col items-center cursor-pointer flex-shrink-0" onClick={() => submittedData && setActiveStep('throttling')} style={{ cursor: submittedData ? 'pointer' : 'not-allowed' }}>
                    <div 
                      className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-white font-semibold transition-colors relative z-10 ${activeStep === 'throttling' && !declarationSubmitted && !isReadOnly ? 'bg-primary-500' : (declarationSubmitted || isReadOnly) ? '' : 'bg-primary-200'}`}
                      style={{ ...(declarationSubmitted || isReadOnly ? { backgroundColor: '#3ADA7A' } : {}), opacity: 1 }}
                    >2</div>
                    <div
                      className={`mt-2 text-sm font-semibold whitespace-nowrap ${
                        (declarationSubmitted || isReadOnly) && activeStep !== 'throttling'
                          ? 'text-primary-500 underline decoration-primary-500 underline-offset-2'
                          : activeStep === 'throttling' || declarationSubmitted
                            ? 'text-black'
                            : 'text-gray-600'
                      }`}
                    >
                      Throttling
                    </div>
                  </div>
                </div>
              </div>

              <div>
                {activeStep === 'configuration' ? (
                  <>
                    <div className="!mt-4 !mb-4 border-t-2 border-[#EFEFEF]" />
                    <div className="opacity-75 pointer-events-none">
                      <form className="flex flex-col" autoComplete="off">
                        <div className="space-y-6">
                          {isInternalAsset && (
                            <AssetExposureSection networkType={networkType} setNetworkType={setNetworkType} showRadios={false}>
                              <VpnProfileSelector selectedVpnProfile={selectedVpnProfile} setSelectedVpnProfile={setSelectedVpnProfile} mainUrl={mainUrl} />
                            </AssetExposureSection>
                          )}
                          {mergedValidateTargetData?.crm_type !== 'wordpress' && (
                            <AuthenticationSection showAuthFields={showAuthFields} setShowAuthFields={setShowAuthFields} register={register} errors={errors} watch={watch} setValue={setValue} headerFields={headerFields} appendHeader={appendHeader} removeHeader={removeHeader} replaceHeaders={replaceHeaders} />
                          )}
                          {mergedValidateTargetData?.crm_type === 'wordpress' && <div className={'!mt-4 border-t-2 border-[#EFEFEF]'} />}
                        </div>
                      </form>
                    </div>
                  </>
                ) : (
                  // Throttling Flow (Read-Only) — matches the web TestConfigurationForm's styled layout
                  <div className="opacity-75 pointer-events-none">
                  <div className="space-y-6">
                    {/* Please Note Section */}
                    <div className="flex items-start gap-1">
                      <LightbulbOutlinedIcon 
                        sx={{ color: '#F97316', fontSize: '20px' }} 
                        className="flex-shrink-0 mt-0.5" 
                      />
                      <div className="text-sm">
                        <span className="font-semibold" style={{ color: '#F97316' }}>Please Note:</span>{' '}
                        <span className="text-gray-800">
                          Exceeding your server's traffic capacity or WAF thresholds can trigger blocking, leading to failed requests or an incomplete test. If you are unsure of your asset's limits, we recommend starting with <strong>Standard</strong>.
                        </span>
                      </div>
                    </div>

                    {/* Test Strategy Section */}
                    <div className="rounded-lg p-5" style={{ background: '#12AFB80D', border: '1px solid #12AFB8' }}>
                      <div className="flex items-center gap-2 mb-2">
                        <SettingsIcon className="text-primary-500" />
                        <div className="tracking-wide font-semibold text-black">Test Strategy</div>
                      </div>
                      <div className="text-sm text-gray-600 mb-4">
                        Choose a preset configuration or customize individual parameters to optimize your test flow.
                      </div>
                      <div className="flex flex-col md:flex-row flex-wrap gap-3">
                        {['stealth', 'standard', 'turbo', 'custom'].map((strategy) => {
                          const buttonContent = (
                            <button
                              onClick={() => {}}
                              disabled={true}
                              className={`flex-1 min-w-0 px-3 py-2 rounded-lg border-2 transition-all flex flex-col items-center justify-center gap-1.5 ${
                                testStrategy === strategy
                                  ? 'bg-primary-500 border-primary-500 text-white'
                                  : 'bg-white border-gray-300'
                              }`}
                              style={testStrategy !== strategy ? { color: '#00000099' } : {}}
                            >
                              <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                                <img src={`/images/genpt/throttle/icons/${strategy === 'custom' ? 'radar' : strategy}.svg`} alt={strategy} className={`w-[18px] h-[18px] object-contain ${testStrategy === strategy ? 'brightness-0 invert' : 'grayscale opacity-70'}`} />
                              </div>
                              <span className="capitalize font-medium text-center text-sm">{strategy}</span>
                            </button>
                          );

                          if (strategy === 'custom') {
                            return <React.Fragment key={strategy}>{buttonContent}</React.Fragment>;
                          }

                          return (
                            <MuiTooltip
                              key={strategy}
                              title={
                                strategy === 'stealth' ? (
                                  <div>
                                    <div className="font-semibold text-[14px] mb-1">Ideal for Production Environments</div>
                                    <ul className="list-disc list-inside text-xs">
                                      <li>Throttles requests to stay below strict detection thresholds.</li>
                                      <li>Minimizes request density to prevent triggering automated blocking rules on sensitive assets</li>
                                    </ul>
                                  </div>
                                ) : strategy === 'standard' ? (
                                  <div>
                                    <div className="font-semibold text-[14px] mb-1">Ideal for Staging/QA Environments</div>
                                    <ul className="list-disc list-inside text-xs">
                                      <li>Maintains a balanced request velocity to ensure timely test completion.</li>
                                      <li>May trigger alerts on strict firewalls; recommended for whitelisted IPs.</li>
                                    </ul>
                                  </div>
                                ) : strategy === 'turbo' ? (
                                  <div>
                                    <div className="font-semibold text-[14px] mb-1">Not Ideal for Standard Environments</div>
                                    <ul className="list-disc list-inside text-xs">
                                      <li>Excessive request volume sent to target assets will likely trigger the WAF to lock out our IP.</li>
                                      <li>Ensure rate-limiting rules are disabled or bypassed.</li>
                                    </ul>
                                  </div>
                                ) : null
                              }
                            >
                              {buttonContent}
                            </MuiTooltip>
                          );
                        })}
                      </div>
                    </div>

                    {/* Expected Outcome Section */}
                    <div className="text-sm text-gray-600">
                      Based on your current settings, here's what to expect from this test run.
                    </div>
                    <div className="border border-gray-200 rounded-lg p-5 bg-white">
                      <div className="flex flex-col items-center">
                        <div className="w-full mb-4 relative flex items-center justify-center">
                          <div className="relative w-full max-w-md flex flex-col items-center">
                            {(() => {
                              const gaugeInfo = getGaugeInfo(rateLimit);
                              let imagePath = '';
                              if (rateLimit >= 1 && rateLimit <= 29) {
                                imagePath = '/images/genpt/throttle/slow.svg';
                              } else if (rateLimit >= 30 && rateLimit <= 89) {
                                imagePath = '/images/genpt/throttle/standard.svg';
                              } else if (rateLimit >= 90 && rateLimit <= 120) {
                                imagePath = '/images/genpt/throttle/fast.svg';
                              } else {
                                imagePath = '/images/genpt/throttle/intense.svg';
                              }
                              return (
                                <img
                                  src={imagePath}
                                  alt={gaugeInfo.label}
                                  className="w-full max-w-[200px] h-auto"
                                />
                              );
                            })()}
                          </div>
                        </div>
                        <div className={`text-sm text-center ${
                          getGaugeInfo(rateLimit).color === 'red' ? 'text-red-600' : 'text-gray-700'
                        }`}>
                          {(() => {
                            const description = getGaugeInfo(rateLimit).description;
                            const parts = description.split(/(slowly|balanced|fast|intensely)/i);
                            return parts.map((part, index) => {
                              const lowerPart = part.toLowerCase();
                              if (lowerPart === 'slowly') {
                                return <span key={index} className="font-semibold" style={{ color: '#3ADA7A' }}>{part}</span>;
                              } else if (lowerPart === 'balanced') {
                                return <span key={index} className="font-semibold" style={{ color: '#FA890E' }}>{part}</span>;
                              } else if (lowerPart === 'fast') {
                                return <span key={index} className="font-semibold" style={{ color: '#F65C5C' }}>{part}</span>;
                              } else if (lowerPart === 'intensely') {
                                return <span key={index} className="font-semibold" style={{ color: '#BA0000' }}>{part}</span>;
                              }
                              return <span key={index}>{part}</span>;
                            });
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* Advanced Parameters Section */}
                    <div className="space-y-4">
                      <div className="text-lg font-semibold text-gray-800">Advanced Parameters</div>

                      {/* Rate Limit */}
                      <div className="py-0.5 px-2 bg-white">
                        <div className="flex items-center justify-between mb-0.5">
                          <div className="flex items-center gap-2">
                            <img src="/images/genpt/throttle/icons/rate_limit.svg" alt="Rate limit" className="w-4 h-4 object-contain flex-shrink-0" />
                            <div className="font-semibold text-black">Rate Limit</div>
                          </div>
                          <div className="font-semibold !text-[16px] text-gray-700 bg-[#F6F9FC] px-2 py-1">
                            <span style={{ fontWeight: 700 }}>{rateLimit}</span> <span className='text-sm' style={{ fontWeight: 300 }}>req/s</span>
                          </div>
                        </div>
                        <div className="text-sm text-gray-600 mb-1">
                          Maximum number of requests sent per second.
                        </div>
                        <div>
                          <Slider
                            value={rateLimit}
                            onChange={() => {}}
                            min={10}
                            max={150}
                            valueLabelDisplay="off"
                            disabled={true}
                            className="w-full"
                            sx={{
                              color: '#12AFB8',
                              height: 10,
                              '& .MuiSlider-thumb': {
                                backgroundColor: 'white',
                                border: '1px solid #12AFB8',
                                width: 12,
                                height: 12,
                              },
                              '& .MuiSlider-track': {
                                height: 10,
                              },
                              '& .MuiSlider-rail': {
                                height: 10,
                              },
                              '&.Mui-disabled': {
                                color: '#12AFB8',
                                opacity: 0.5,
                              },
                            }}
                          />
                        </div>
                        <div className="rounded-lg border border-[#FFE0B2]/80 bg-[#FFF3E0] p-2">
                          <div className="flex items-start gap-2">
                            <img src="/images/genpt/throttle/icons/info.svg" alt="Info" className="w-4 h-4 object-contain flex-shrink-0 mt-0.5" />
                            <div className="text-xs text-gray-700">
                              Higher limits increase speed but may trigger rate limits or overload the target server. Lower limits generally ensure uninterrupted tests.
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Concurrency */}
                      <div className="py-0.5 px-2 bg-white">
                        <div className="flex items-center justify-between mb-0.5">
                          <div className="flex items-center gap-2">
                            <img src="/images/genpt/throttle/icons/concurrency.svg" alt="Concurrency" className="w-4 h-4 object-contain flex-shrink-0" />
                            <div className="font-semibold text-black">Concurrency</div>
                          </div>
                          <div className="text-sm text-gray-700 bg-[#F6F9FC] px-2 py-1">
                            <span style={{ fontWeight: 700 }}>{concurrency}</span>
                          </div>
                        </div>
                        <div className="text-sm text-gray-600 mb-1">
                          Number of test cases in parallel.
                        </div>
                        <div>
                          <Slider
                            value={concurrency}
                            onChange={() => {}}
                            min={1}
                            max={20}
                            valueLabelDisplay="off"
                            disabled={true}
                            className="w-full"
                            sx={{
                              color: '#12AFB8',
                              height: 10,
                              '& .MuiSlider-thumb': {
                                backgroundColor: 'white',
                                border: '1px solid #12AFB8',
                                width: 12,
                                height: 12,
                              },
                              '& .MuiSlider-track': {
                                height: 10,
                              },
                              '& .MuiSlider-rail': {
                                height: 10,
                              },
                              '&.Mui-disabled': {
                                color: '#12AFB8',
                                opacity: 0.5,
                              },
                            }}
                          />
                        </div>
                        <div className="rounded-lg border border-[#FFE0B2]/80 bg-[#FFF3E0] p-2">
                          <div className="flex items-start gap-2">
                            <img src="/images/genpt/throttle/icons/info.svg" alt="Info" className="w-4 h-4 object-contain flex-shrink-0 mt-0.5" />
                            <div className="text-xs text-gray-700">
                              Higher concurrency speeds up testing but may lead to higher load on the asset. Lower concurrency is safer for fragile or resource-limited targets.
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Request Timeout */}
                      <div className="py-0.5 px-2 bg-white">
                        <div className="flex items-center justify-between mb-0.5">
                          <div className="flex items-center gap-2">
                            <img src="/images/genpt/throttle/icons/timeout.svg" alt="Timeout" className="w-4 h-4 object-contain flex-shrink-0" />
                            <div className="font-semibold text-black">Request Timeout</div>
                          </div>
                          <div className="font-semibold !text-[16px] text-gray-700 bg-[#F6F9FC] px-2 py-1">
                            <span style={{ fontWeight: 700 }}>{String(requestTimeout).padStart(2, '0')}</span> <span className='text-sm' style={{ fontWeight: 300 }}>sec</span>
                          </div>
                        </div>
                        <div className="text-sm text-gray-600 mb-1">
                          How long the system waits for a response before marking a request as failed.
                        </div>
                        <div>
                          <Slider
                            value={requestTimeout}
                            onChange={() => {}}
                            min={1}
                            max={10}
                            valueLabelDisplay="off"
                            disabled={true}
                            className="w-full"
                            sx={{
                              color: '#12AFB8',
                              height: 10,
                              '& .MuiSlider-thumb': {
                                backgroundColor: 'white',
                                border: '1px solid #12AFB8',
                                width: 12,
                                height: 12,
                              },
                              '& .MuiSlider-track': {
                                height: 10,
                              },
                              '& .MuiSlider-rail': {
                                height: 10,
                              },
                              '&.Mui-disabled': {
                                color: '#12AFB8',
                                opacity: 0.5,
                              },
                            }}
                          />
                        </div>
                        <div className="rounded-lg border border-[#FFE0B2]/80 bg-[#FFF3E0] p-2">
                          <div className="flex items-start gap-2">
                            <img src="/images/genpt/throttle/icons/info.svg" alt="Info" className="w-4 h-4 object-contain flex-shrink-0 mt-0.5" />
                            <div className="text-xs text-gray-700">
                              A longer timeout helps with slow servers. A shorter timeout speeds up the test on fast servers.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className={'!mt-4 border-t-2 border-[#EFEFEF]'} />
                  </div>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      ) : (
        <>
          {!scheduleModalSeparatedSteps && (
            <div className="flex items-center text-lg font-semibold text-gray-800 mb-4">
              <HandymanIcon className="mr-2 text-primary-500" /> Test Setup
            </div>
          )}

          {/* Stepper — GenPT chat only. Schedule API modal: stepper lives inside post-collection card (mockup). */}
          {!scheduleModalSeparatedSteps ? (
            <div
              className={
                embeddedInModal
                  ? 'mt-3 mb-3 flex justify-center rounded-xl bg-gray-50 px-4 py-3'
                  : 'mt-4 mb-4 bg-gray-50 rounded-lg p-4 flex justify-center'
              }
            >
              {renderApiScheduleThreeStepper()}
            </div>
          ) : null}

          {/* Content Section */}
          <div>
            {scheduleModalPostCollection && (
              <button
                type="button"
                onClick={() => !declarationSubmitted && setActiveStep('collection')}
                disabled={declarationSubmitted}
                className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 hover:underline disabled:pointer-events-none disabled:opacity-40 disabled:no-underline"
              >
                <span aria-hidden>←</span> Go Back
              </button>
            )}
            <div
              className={
                scheduleModalPostCollection
                  ? 'rounded-xl border border-[#EFEFEF] bg-white p-4 shadow-sm'
                  : 'contents'
              }
            >
            {scheduleModalPostCollection && (
              <div className="mb-6 flex justify-center rounded-lg bg-gray-50 px-3 py-3">
                {renderApiScheduleThreeStepper()}
              </div>
            )}
            {embeddedInModal && activeStep === 'collection' ? (
              scheduleModalSeparatedSteps ? (
                <div className="rounded-xl border border-[#EFEFEF] bg-white p-4 shadow-sm">
                  <APICollectionSourceWidget
                    msg={msg}
                    targetURL={finalTargetURL}
                    chatId={null}
                    sessionId={null}
                    sessionDetails={scheduleModalCollectionSessionDetails}
                    embeddedInScheduleModal
                    scheduleModalPrimaryCtaLabel="SETUP CONFIG"
                    onScheduleCollectionComplete={handleScheduleModalCollectionComplete}
                  />
                </div>
              ) : (
                <APICollectionSourceWidget
                  msg={msg}
                  targetURL={finalTargetURL}
                  chatId={null}
                  sessionId={null}
                  sessionDetails={scheduleModalCollectionSessionDetails}
                  embeddedInScheduleModal
                  onScheduleCollectionComplete={handleScheduleModalCollectionComplete}
                />
              )
            ) : activeStep === 'configuration' ? (
          <>
            {scheduleModalPostCollection && (
              <div className="mb-4 flex items-center text-lg font-semibold text-gray-800">
                <HandymanIcon className="mr-2 text-primary-500" /> Test Setup
              </div>
            )}
            {scheduleModalSeparatedSteps && !scheduleModalPostCollection && (
              <div className="mb-3 flex items-center text-lg font-semibold text-gray-800">
                <HandymanIcon className="mr-2 text-primary-500" /> Test Setup
              </div>
            )}
            <div
              className={
                scheduleModalPostCollection
                  ? undefined
                  : scheduleModalSeparatedSteps
                    ? 'rounded-xl border border-[#EFEFEF] bg-white p-4 shadow-sm'
                    : 'contents'
              }
            >
          <div className={declarationSubmitted ? 'opacity-75 pointer-events-none' : ''}>
            <form className="flex flex-col" autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-6">
              {isInternalAsset && (
                <AssetExposureSection
                  networkType={networkType}
                  setNetworkType={setNetworkType}
                  showRadios={false}
                >
                  <VpnProfileSelector
                    selectedVpnProfile={selectedVpnProfile}
                    setSelectedVpnProfile={setSelectedVpnProfile}
                    mainUrl={mainUrl}
                  />
                </AssetExposureSection>
              )}

              {mergedValidateTargetData?.crm_type !== 'wordpress' && (
                <>
                  <AuthenticationSection
                    showAuthFields={showAuthFields}
                    setShowAuthFields={setShowAuthFields}
                    register={register}
                    errors={errors}
                    watch={watch}
                    setValue={setValue}
                    headerFields={headerFields}
                    appendHeader={appendHeader}
                    removeHeader={removeHeader}
                    replaceHeaders={replaceHeaders}
                  />
                </>
              )}

              {mergedValidateTargetData?.crm_type === 'wordpress' && (
                <div className={'!mt-4 border-t-2 border-[#EFEFEF]'} />
              )}
            </div>

            <div className={'!mt-4 border-t-2 border-[#EFEFEF]'} />

            <div className="flex flex-wrap justify-end gap-3 sm:gap-5 mt-6">
              <Button
                type="submit"
                disabled={
                  declarationSubmitted ||
                  continueIsLoading ||
                  !isFormValid ||
                  (embeddedInModal && !scheduleModalApiCollection)
                }
                className={`flex items-center gap-2 px-4 ${
                  declarationSubmitted ||
                  continueIsLoading ||
                  !isFormValid ||
                  (embeddedInModal && !scheduleModalApiCollection)
                    ? '!bg-gray-50 !text-gray-500 cursor-not-allowed'
                    : '!bg-primary-500 hover:!bg-primary-600 !text-white cursor-pointer'
                }`}
              >
                {continueIsLoading ? <AutorenewIcon className="animate-spin text-white" /> : 'Continue'}
              </Button>
              {embeddedInModal &&
                scheduleModalApiCollection &&
                !scheduleModalPostCollection && (
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => setActiveStep('collection')}
                  disabled={declarationSubmitted || continueIsLoading}
                  className="!border-primary-500 !text-primary-500 hover:!bg-primary-50"
                >
                  Back
                </Button>
              )}
              {!(embeddedInModal && hideScheduleTab) && (
                <Button variant="outlined" onClick={handleClose} disabled={declarationSubmitted || continueIsLoading}>
                  Cancel
                </Button>
              )}
              </div>
            </form>
          </div>
            </div>
          </>
            ) : activeStep === 'throttling' ? (
              <>
              {scheduleModalSeparatedSteps && !scheduleModalPostCollection && (
                <div className="mb-3 flex items-center text-lg font-semibold text-gray-800">
                  <SettingsIcon className="mr-2 text-primary-500" /> Throttling
                </div>
              )}
              <div
                className={
                  scheduleModalPostCollection
                    ? undefined
                    : scheduleModalSeparatedSteps
                      ? 'rounded-xl border border-[#EFEFEF] bg-white p-4 shadow-sm'
                      : 'contents'
                }
              >
              <div
                className={
                  declarationSubmitted || pipelineThrottleSaving ? 'opacity-75 pointer-events-none' : ''
                }
              >
                <div className="space-y-6">
                {/* Please Note Section */}
                <div className="flex items-start gap-1">
                  <LightbulbOutlinedIcon
                    sx={{ color: '#F97316', fontSize: '20px' }}
                    className="flex-shrink-0 mt-0.5"
                  />
                  <div className="text-sm">
                    <span className="font-semibold" style={{ color: '#F97316' }}>Please Note:</span>{' '}
                    <span className="text-gray-800">
                      Exceeding your server&apos;s traffic capacity or WAF thresholds can trigger blocking, leading to failed requests or an incomplete test. If you are unsure of your asset&apos;s limits, we recommend starting with <strong>Standard</strong>.
                    </span>
                  </div>
                </div>

                {/* Test Strategy Section */}
                <div className="rounded-lg p-5" style={{ background: '#12AFB80D', border: '1px solid #12AFB8' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <SettingsIcon className="text-primary-500" />
                    <div className="tracking-wide font-semibold text-black">Test Strategy</div>
                  </div>
                  <div className="text-sm text-gray-600 mb-4">
                    Choose a preset configuration or customize individual parameters to optimize your test flow.
                  </div>
                  <div className="flex flex-col md:flex-row flex-wrap gap-3">
                    {['stealth', 'standard', 'turbo', 'custom'].map((strategy) => {
                      const isDisabled = apiThrottleLockedByConfig;
                      const buttonContent = (
                        <button
                          onClick={() => !isDisabled && handlePresetSelect(strategy)}
                          disabled={isDisabled}
                          className={`flex-1 min-w-0 px-3 py-2 rounded-lg border-2 transition-all flex flex-col items-center justify-center gap-1.5 ${
                            testStrategy === strategy
                              ? 'bg-primary-500 border-primary-500 text-white'
                              : 'bg-white border-gray-300 hover:border-primary-300'
                          } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                          style={testStrategy !== strategy ? { color: '#00000099' } : {}}
                        >
                          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                            <img
                              src={`/images/genpt/throttle/icons/${strategy === 'custom' ? 'radar' : strategy}.svg`}
                              alt={strategy}
                              className={`w-[18px] h-[18px] object-contain ${testStrategy === strategy ? 'brightness-0 invert' : 'grayscale opacity-70'}`}
                            />
                          </div>
                          <span className="capitalize font-medium text-center text-sm">{strategy}</span>
                        </button>
                      );

                      if (strategy === 'custom') {
                        return <React.Fragment key={strategy}>{buttonContent}</React.Fragment>;
                      }

                      return (
                        <MuiTooltip
                          key={strategy}
                          title={
                            strategy === 'stealth' ? (
                              <div>
                                <div className="font-semibold text-[14px] mb-1">Ideal for Production Environments</div>
                                <ul className="list-disc list-inside text-xs">
                                  <li>Throttles requests to stay below strict detection thresholds.</li>
                                  <li>Minimizes request density to prevent triggering automated blocking rules on sensitive assets</li>
                                </ul>
                              </div>
                            ) : strategy === 'standard' ? (
                              <div>
                                <div className="font-semibold text-[14px] mb-1">Ideal for Staging/QA Environments</div>
                                <ul className="list-disc list-inside text-xs">
                                  <li>Maintains a balanced request velocity to ensure timely test completion.</li>
                                  <li>May trigger alerts on strict firewalls; recommended for whitelisted IPs.</li>
                                </ul>
                              </div>
                            ) : strategy === 'turbo' ? (
                              <div>
                                <div className="font-semibold text-[14px] mb-1">Not Ideal for Standard Environments</div>
                                <ul className="list-disc list-inside text-xs">
                                  <li>Excessive request volume sent to target assets will likely trigger the WAF to lock out our IP.</li>
                                  <li>Ensure rate-limiting rules are disabled or bypassed.</li>
                                </ul>
                              </div>
                            ) : null
                          }
                        >
                          {buttonContent}
                        </MuiTooltip>
                      );
                    })}
                  </div>
                </div>

                {/* Expected Outcome Section */}
                <div className="text-sm text-gray-600">
                  Based on your current settings, here&apos;s what to expect from this test run.
                </div>
                <div className="border border-gray-200 rounded-lg p-5 bg-white">
                  <div className="flex flex-col items-center">
                    {/* Throttle illustration */}
                    <div className="w-full mb-4 relative flex items-center justify-center">
                      <div className="relative w-full max-w-md flex flex-col items-center">
                        {(() => {
                          const gaugeInfo = getGaugeInfo(rateLimit);
                          let imagePath = '';

                          if (rateLimit >= 1 && rateLimit <= 29) {
                            imagePath = '/images/genpt/throttle/slow.svg';
                          } else if (rateLimit >= 30 && rateLimit <= 89) {
                            imagePath = '/images/genpt/throttle/standard.svg';
                          } else if (rateLimit >= 90 && rateLimit <= 120) {
                            imagePath = '/images/genpt/throttle/fast.svg';
                          } else {
                            imagePath = '/images/genpt/throttle/intense.svg';
                          }

                          return (
                            <img
                              src={imagePath}
                              alt={gaugeInfo.label}
                              className="w-full max-w-[200px] h-auto"
                            />
                          );
                        })()}
                      </div>
                    </div>
                    <div
                      className={`text-sm text-center ${
                        getGaugeInfo(rateLimit).color === 'red' ? 'text-red-600' : 'text-gray-700'
                      }`}
                    >
                      {(() => {
                        const description = getGaugeInfo(rateLimit).description;
                        const parts = description.split(/(slowly|balanced|fast|intensely)/i);
                        return parts.map((part, index) => {
                          const lowerPart = part.toLowerCase();
                          if (lowerPart === 'slowly') {
                            return <span key={index} className="font-semibold" style={{ color: '#3ADA7A' }}>{part}</span>;
                          } else if (lowerPart === 'balanced') {
                            return <span key={index} className="font-semibold" style={{ color: '#FA890E' }}>{part}</span>;
                          } else if (lowerPart === 'fast') {
                            return <span key={index} className="font-semibold" style={{ color: '#F65C5C' }}>{part}</span>;
                          } else if (lowerPart === 'intensely') {
                            return <span key={index} className="font-semibold" style={{ color: '#BA0000' }}>{part}</span>;
                          }
                          return <span key={index}>{part}</span>;
                        });
                      })()}
                    </div>
                  </div>
                </div>

                {/* Advanced Parameters Section */}
                <div className="space-y-4">
                  <div className="text-lg font-semibold text-gray-800">Advanced Parameters</div>

                  {/* Rate Limit */}
                  <div className="py-0.5 px-2 bg-white">
                    <div className="flex items-center justify-between mb-0.5">
                      <div className="flex items-center gap-2">
                        <img src="/images/genpt/throttle/icons/rate_limit.svg" alt="Rate limit" className="w-4 h-4 object-contain flex-shrink-0" />
                        <div className="font-semibold text-black">Rate Limit</div>
                      </div>
                      <div className="font-semibold !text-[16px] text-gray-700 bg-[#F6F9FC] px-2 py-1">
                        <span style={{ fontWeight: 700 }}>{rateLimit}</span> <span className="text-sm" style={{ fontWeight: 300 }}>req/s</span>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 mb-1">
                      Maximum number of requests sent per second.
                    </div>
                    <div>
                      <Slider
                        value={rateLimit}
                        onChange={(e, newValue) => handleRateLimitChange(newValue)}
                        min={10}
                        max={150}
                        valueLabelDisplay="off"
                        disabled={apiThrottleLockedByConfig}
                        className="w-full"
                        sx={{
                          color: '#12AFB8',
                          height: 10,
                          '& .MuiSlider-thumb': {
                            backgroundColor: 'white',
                            border: '1px solid #12AFB8',
width: 12,
height: 12,
                            '&:hover': {
                              boxShadow: '0 0 0 6px rgba(18, 175, 184, 0.16)',
                            },
                          },
                          '& .MuiSlider-track': {
                            height: 10,
                          },
                          '& .MuiSlider-rail': {
                            height: 10,
                          },
                          '&.Mui-disabled': {
                            color: '#12AFB8',
                            opacity: 0.5,
                          },
                        }}
                      />
                    </div>
                    <div className="rounded-lg border border-[#FFE0B2]/80 bg-[#FFF3E0] p-2">
                      <div className="flex items-start gap-2">
                        <img src="/images/genpt/throttle/icons/info.svg" alt="Info" className="w-4 h-4 object-contain flex-shrink-0 mt-0.5" />
                        <div className="text-xs text-gray-700 ">
                          Higher limits increase speed but may trigger rate limits or overload the target server. Lower limits generally ensure uninterrupted tests.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Concurrency */}
                  <div className="py-0.5 px-2 bg-white">
                    <div className="flex items-center justify-between mb-0.5">
                      <div className="flex items-center gap-2">
                        <img src="/images/genpt/throttle/icons/concurrency.svg" alt="Concurrency" className="w-4 h-4 object-contain flex-shrink-0" />
                        <div className="font-semibold text-black">Concurrency</div>
                      </div>
                      <div className="text-sm text-gray-700 bg-[#F6F9FC] px-2 py-1">
                        <span style={{ fontWeight: 700 }}>{concurrency}</span>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 mb-1">
                      Number of test cases in parallel.
                    </div>
                    <div>
                      <Slider
                        value={concurrency}
                        onChange={(e, newValue) => handleConcurrencyChange(newValue)}
                        min={1}
                        max={20}
                        valueLabelDisplay="off"
                        disabled={apiThrottleLockedByConfig}
                        className="w-full"
                        sx={{
                          color: '#12AFB8',
                          height: 10,
                          '& .MuiSlider-thumb': {
                            backgroundColor: 'white',
                            border: '1px solid #12AFB8',
width: 12,
height: 12,
                            '&:hover': {
                              boxShadow: '0 0 0 6px rgba(18, 175, 184, 0.16)',
                            },
                          },
                          '& .MuiSlider-track': {
                            height: 10,
                          },
                          '& .MuiSlider-rail': {
                            height: 10,
                          },
                          '&.Mui-disabled': {
                            color: '#12AFB8',
                            opacity: 0.5,
                          },
                        }}
                      />
                    </div>
                    <div className="rounded-lg border border-[#FFE0B2]/80 bg-[#FFF3E0] p-2">
                      <div className="flex items-start gap-2">
                        <img src="/images/genpt/throttle/icons/info.svg" alt="Info" className="w-4 h-4 object-contain flex-shrink-0 mt-0.5" />
                        <div className="text-xs text-gray-700 ">
                          Higher concurrency speeds up testing but may lead to higher load on the asset. Lower concurrency is safer for fragile or resource-limited targets.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Request Timeout */}
                  <div className="py-0.5 px-2 bg-white">
                    <div className="flex items-center justify-between mb-0.5">
                      <div className="flex items-center gap-2">
                        <img src="/images/genpt/throttle/icons/timeout.svg" alt="Timeout" className="w-4 h-4 object-contain flex-shrink-0" />
                        <div className="font-semibold text-black">Request Timeout</div>
                      </div>
                      <div className="font-semibold !text-[16px] text-gray-700 bg-[#F6F9FC] px-2 py-1">
                        <span style={{ fontWeight: 700 }}>{String(requestTimeout).padStart(2, '0')}</span> <span className="text-sm" style={{ fontWeight: 300 }}>sec</span>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 mb-1">
                      How long the system waits for a response before marking a request as failed.
                    </div>
                    <div>
                      <Slider
                        value={requestTimeout}
                        onChange={(e, newValue) => handleTimeoutChange(newValue)}
                        min={1}
                        max={10}
                        valueLabelDisplay="off"
                        disabled={apiThrottleLockedByConfig}
                        className="w-full"
                        sx={{
                          color: '#12AFB8',
                          height: 10,
                          '& .MuiSlider-thumb': {
                            backgroundColor: 'white',
                            border: '1px solid #12AFB8',
width: 12,
height: 12,
                            '&:hover': {
                              boxShadow: '0 0 0 6px rgba(18, 175, 184, 0.16)',
                            },
                          },
                          '& .MuiSlider-track': {
                            height: 10,
                          },
                          '& .MuiSlider-rail': {
                            height: 10,
                          },
                          '&.Mui-disabled': {
                            color: '#12AFB8',
                            opacity: 0.5,
                          },
                        }}
                      />
                    </div>
                    <div className="rounded-lg border border-[#FFE0B2]/80 bg-[#FFF3E0] p-2">
                      <div className="flex items-start gap-2">
                        <img src="/images/genpt/throttle/icons/info.svg" alt="Info" className="w-4 h-4 object-contain flex-shrink-0 mt-0.5" />
                        <div className="text-xs text-gray-700 ">
                          A longer timeout helps with slow servers. A shorter timeout speeds up the test on fast servers.
                        </div>
                      </div>
                </div>
              </div>
                </div>

                <div className={'!mt-4 border-t-2 border-[#EFEFEF]'} />

                {/* Start test immediately vs open scheduling step (Schedule GenPT modal: schedule only) */}
                <div className="flex flex-wrap justify-start gap-3 mt-6">
                  {!embeddedInModal && (
                    <Button
                      onClick={handleContinueToDeclaration}
                      disabled={
                        declarationSubmitted ||
                        !submittedData ||
                        rateLimit < 10 ||
                        concurrency < 1 ||
                        requestTimeout < 1
                      }
                      className={`flex items-center gap-2 px-4 py-3 ${
                        declarationSubmitted ||
                        !submittedData ||
                        rateLimit < 10 ||
                        concurrency < 1 ||
                        requestTimeout < 1
                          ? '!bg-gray-50 !text-gray-500 cursor-not-allowed'
                          : '!bg-primary-500 hover:!bg-primary-600 !text-white cursor-pointer'
                      }`}
                    >
                      START TEST
                    </Button>
                  )}
                  <Button
                    variant={embeddedInModal ? undefined : 'outlined'}
                    onClick={handleStartAndScheduleClick}
                    disabled={
                      declarationSubmitted ||
                      pipelineThrottleSaving ||
                      !submittedData ||
                      rateLimit < 10 ||
                      concurrency < 1 ||
                      requestTimeout < 1
                    }
                    className={`flex items-center gap-2 px-4 py-3 ${
                      declarationSubmitted ||
                      pipelineThrottleSaving ||
                      !submittedData ||
                      rateLimit < 10 ||
                      concurrency < 1 ||
                      requestTimeout < 1
                        ? embeddedInModal
                          ? '!bg-gray-50 !text-gray-500 cursor-not-allowed'
                          : '!border-gray-300 !text-gray-500 cursor-not-allowed'
                        : embeddedInModal
                          ? '!bg-primary-500 hover:!bg-primary-600 !text-white cursor-pointer'
                          : '!border-primary-500 !text-primary-500 hover:!bg-primary-50 cursor-pointer'
                    }`}
                  >
                    {embeddedInModal
                      ? hideScheduleTab
                        ? 'SAVE CONFIGURATION'
                        : 'CONTINUE'
                      : 'START & SCHEDULE'}
                  </Button>
                </div>
              </div>
              </div>
              </div>
            </>
            ) : (
              <>
                {scheduleModalSeparatedSteps && !scheduleModalPostCollection && (
                  <div className="mb-3 flex items-center text-lg font-semibold text-gray-800">
                    <CalendarTodayIcon className="mr-2 text-primary-500" />
                    Schedule
                  </div>
                )}
                <div
                  className={
                    scheduleModalPostCollection
                      ? undefined
                      : scheduleModalSeparatedSteps
                        ? 'rounded-xl border border-[#EFEFEF] bg-white p-4 shadow-sm'
                        : 'contents'
                  }
                >
              <ScheduleTest
                initialValues={submittedData?.schedule || {}}
                isReadOnly={false}
                isDisabled={declarationSubmitted}
                onSubmit={handleScheduleSubmit}
                submitButtonLabel={scheduleStepSubmitLabel}
                assetId={msg?.metadata?.asset?.id ?? submittedData?.assetId ?? null}
                excludeScheduleId={editingScheduleId}
                lockStartDate={Boolean(editingScheduleId)}
              />
                </div>
              </>
            )}
            </div>
          </div>

          {/* High Rate Limit Confirmation Modal */}
          {showHighRateModal && (
            <div className="fixed inset-0 bg-[#00000080] flex items-center justify-center z-50 shadow-lg rounded-lg">
              <div className="bg-white rounded-lg p-6 max-w-lg mx-4">
                <div className="flex items-center gap-3 mb-4">
                  <WarningAmberIcon className="text-[#F65C5C] text-2xl" />
                  <h3 className="text-lg font-semibold text-gray-800">Confirm Throttling Settings</h3>
                </div>
                <p className="text-gray-700 mb-6">
                  You’re about to run this test with a request rate of <strong>120+ req/s</strong>, exceeding recommended thresholds and potentially impacting the target environment.
                </p>
                <div className="my-1 rounded-lg border border-[#F65C5C]/40 bg-[#F65C5C]/10 p-2 text-black">
                  <div><span className="font-semibold">Warning:</span> These settings may:</div>
                  <ul className="list-disc list-inside ml-2 mt-1 space-y-1">
                    <li>Trigger WAF to restricted or block our IP.</li>
                    <li>Increase traffic and impact application‘s performance.</li>
                    <li>Produce incomplete or inaccurate results.</li>
                  </ul>
                </div>
                <div className="my-4">
                  Are you sure you want to proceed with these settings?
                </div>
                <div className="flex justify-center gap-3">
                  <Button
                    variant="contained"
                    onClick={() => {
                      setShowHighRateModal(false);
                      if (pendingAction) {
                        pendingAction();
                        setPendingAction(null);
                      }
                    }}
                  >
                    Continue
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setShowHighRateModal(false);
                      setPendingAction(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

    </div>
    <DeclarationModal
      open={declarationModalOpen}
      setOpen={handleDeclarationModalSetOpen}
      targetURL={finalTargetURL}
      onConfirm={handleDeclarationModalConfirm}
      isLoading={declarationModalLoading}
      onDiscardFlow={embeddedInModal && onDeclarationDiscard ? onDeclarationDiscard : undefined}
      confirmButtonLabel="CONFIRM"
    />
    </>
  );
}

export default APIConfigurationForm;
// Asset Exposure Section
const AssetExposureSection = ({ networkType, setNetworkType, children, showRadios = true }) => {
  return (
    <div className="my-4">
      <div className="">
        <div className="flex items-center gap-2 mb-2">
          <div>
            <PublicIcon className="text-primary-500" />
          </div>
          <div className="tracking-wide font-semibold text-black">Asset Network</div>
          <div>
            <Info
              tooltip={
                "This setting is crucial for a successful scan. It tells our testing engine how to locate and connect to your target asset.\n\nIn cybersecurity, Asset Exposure refers to how visible and accessible a digital asset is to potential attackers. Correctly defining this is the first step in simulating a real-world test.\n\nPlease choose the option that best describes your asset's network location:"
              }
            />
          </div>
        </div>
        {showRadios ? (
          <>
            <div className="">Select network exposure of the asset</div>
            <RadioGroup
              row
              value={networkType}
              onChange={(e) => setNetworkType(e.target.value)}
              sx={{ alignItems: 'center' }}
            >
              <FormControlLabel
                value="external"
                control={<Radio sx={{ '&.Mui-checked': { color: '#228EBC' } }} />}
                label={
                  <span className="inline-flex items-center gap-1">
                    External
                    <Info
                      tooltip={
                        "Select this if your asset is publicly available on the internet. No special connection is required for our scanner to reach it.\n\nExample: Your company's public-facing website."
                      }
                    />
                  </span>
                }
              />

              <FormControlLabel
                value="internal"
                control={<Radio sx={{ '&.Mui-checked': { color: '#228EBC' } }} />}
                label={
                  <span className="inline-flex items-center gap-1">
                    Internal
                    <Info
                      tooltip={
                        'Select this if your asset is on a private network (like a corporate staging server or an internal admin panel) and is not accessible from the public internet.\n\nTo scan an internal asset, you must select the correct VPN profile and ensure your connection is active before starting the test.'
                      }
                    />
                  </span>
                }
              />
            </RadioGroup>
          </>
        ) : (
          <div className="text-sm text-gray-600">
            Select VPN profile from saved profiles
          </div>
        )}
      </div>

      {children}
    </div>
  );
};

// VPN Profile Selector
const VpnProfileSelector = ({ selectedVpnProfile, setSelectedVpnProfile, mainUrl }) => {
  const [openVpn, setOpenVpn] = useState(false);
  const [searchVpn, setSearchVpn] = useState('');
  const [isReloading, setIsReloading] = useState(false);

  const stop = (e) => e.stopPropagation();

  const { data = {}, isLoading, refetch } = useIntegrationDetails(ProviderType.OPENVPN);
  
  const allProfilesVpn = useMemo(() => {
    if (Array.isArray(data)) {
      return data;
    }
    if (data?.IntegrationData?.details && Array.isArray(data.IntegrationData.details)) {
      return data.IntegrationData.details;
    }
    if (data?.IntegrationDetails && Array.isArray(data.IntegrationDetails)) {
      return data.IntegrationDetails;
    }
    if (data?.data && Array.isArray(data.data)) {
      return data.data;
    }
    if (data?.profiles && Array.isArray(data.profiles)) {
      return data.profiles;
    }
    return [];
  }, [data]);

  const handleReload = async () => {
    setIsReloading(true);
    try {
      await refetch();
    } finally {
      setIsReloading(false);
    }
  };

  const filteredProfilesVpn = useMemo(() => {
    const q = normalizeVpn(searchVpn);
    if (!q) return allProfilesVpn;
    return allProfilesVpn.filter((rec) => {
      const id = rec?.id || '';
      const name = rec?.providerData?.profileName || '';
      const profilePath = rec?.providerData?.profilePath || '';
      const providerType = rec?.providerType || '';
      const createdBy = rec?.createdBy || '';
      const status = rec?.status || '';

      return (
        normalizeVpn(id).includes(q) ||
        normalizeVpn(name).includes(q) ||
        normalizeVpn(profilePath).includes(q) ||
        normalizeVpn(providerType).includes(q) ||
        normalizeVpn(createdBy).includes(q) ||
        normalizeVpn(status).includes(q)
      );
    });
  }, [allProfilesVpn, searchVpn]);

  const updateSelectedVpn = (item) => {
    setSelectedVpnProfile(item);
  };

  const isVpnEmpty = (filteredProfilesVpn?.length ?? 0) === 0;

  useEffect(() => {
    if (isVpnEmpty && selectedVpnProfile) {
      setSelectedVpnProfile(null);
    }
  }, [isVpnEmpty, selectedVpnProfile, setSelectedVpnProfile]);

  return (
    <div>
      <div className="w-[80%] flex items-center gap-2">
        <div className="flex-1">
          <MuiTextField
            label=""
            select
            disabled={isVpnEmpty}
            className="text-left"
            onClick={isVpnEmpty ? undefined : () => setOpenVpn((p) => !p)}
            SelectProps={{
              multiple: false,
              value: selectedVpnProfile || '',
              displayEmpty: true,
              renderValue: (value) => {
                if (isVpnEmpty) {
                  return <span style={{ color: '#9CA3AF' }}>No VPN profiles found</span>;
                }
                if (!value) {
                  return <span style={{ color: '#9CA3AF' }}>Choose VPN Profile</span>;
                }
                // Handle case where value is an object with profile_name (from backend)
                if (typeof value === 'object' && value.profile_name) {
                  return value.profile_name;
                }
                // Try to get name from value object first
                let label = value?.providerData?.profileName;
                // If not found, try to find matching profile from allProfilesVpn
                if (!label) {
                  const profileId = value?.id || value?.profile_id;
                  // Only proceed if profileId is a string/number, not an object
                  if (profileId && typeof profileId !== 'object' && allProfilesVpn.length > 0) {
                    const matchedProfile = allProfilesVpn.find(profile => 
                      String(profile.id) === String(profileId)
                    );
                    label = matchedProfile?.providerData?.profileName;
                  }
                }
                // Fallback to ID if name still not found, but ensure it's a string
                if (!label) {
                  const profileId = value?.id || value?.profile_id;
                  label = profileId && typeof profileId !== 'object' ? String(profileId) : null;
                }
                // Ensure label is always a string or null
                return label && typeof label === 'string' ? label : <span style={{ color: '#9CA3AF' }}>Choose VPN Profile</span>;
              },
              open: !isVpnEmpty && openVpn,
              onOpen: () => !isVpnEmpty && setOpenVpn(true),
              onClose: (e, reason) => {
                if (reason === 'escapeKeyDown' || reason === 'backdropClick') setOpenVpn(false);
              },
              MenuProps: {
                PaperProps: { style: { maxHeight: 275 } },
                MenuListProps: { style: { padding: 0 } },
                disableAutoFocusItem: true,
                autoFocus: false,
              },
            }}
          >
          {!isVpnEmpty && (
            <div className="p-2.5 sticky top-0 bg-white z-10" onMouseDown={stop} onClick={stop}>
              <div className="border border-grey1 rounded-[5px] p-1 w-full flex">
                <SearchIcon />
                <input
                  onChange={(e) => setSearchVpn(e?.target?.value?.toLowerCase())}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter' && filteredProfilesVpn.length > 0) {
                      updateSelectedVpn(filteredProfilesVpn[0]);
                      setOpenVpn(false);
                    }
                  }}
                  autoFocus
                  className="inline-block align-middle flex-grow ml-4 outline-none w-full"
                  placeholder="Search here..."
                  value={searchVpn}
                  onMouseDown={stop}
                  onClick={stop}
                />
              </div>
            </div>
          )}

          {!isVpnEmpty && (
            <div>
              {(filteredProfilesVpn || []).map((item, index) => {
                const vpnName = item?.providerData?.profileName || '';
                const isSelected = selectedVpnProfile?.id === item?.id;
                return (
                  <React.Fragment key={`${item?.id}-${index}`}>
                    <MenuItem
                      onKeyDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        updateSelectedVpn(item);
                        setOpenVpn(false);
                      }}
                      selected={isSelected}
                      className={isSelected ? '!bg-primary-500 !text-white' : ''}
                    >
                      <div className="flex flex-col p-2">
                        <Typography variant="caption" className="self-start">
                          {vpnName || item?.id}
                        </Typography>
                      </div>
                    </MenuItem>
                    <div className="!m-0 border-t-2 border-[#EFEFEF]" />
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </MuiTextField>
        </div>
        <button
          type="button"
          onClick={handleReload}
          disabled={isReloading || isLoading}
          className="p-2 mt-2 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1 min-w-[90px] flex-shrink-0"
          title="Reload VPN profiles"
        >
          <AutorenewIcon className={`text-primary-500 ${isReloading || isLoading ? 'animate-spin' : ''}`} />
          {!(isReloading || isLoading) && (
            <span className="text-sm text-primary-500">Refresh</span>
          )}
        </button>
      </div>

      <div className="mt-2">
        <div className="text-grey text-sm">
          <span className="text-[16px] mr-1">
            <WarningAmberIcon fontSize="inherit" color="warning" />
          </span>
          <span className="text-[#ED6C02]">Please Note:</span> Please set up a VPN profile in the platform before running the GenPT test on an internal asset. If you don't have one, please set it up
          {' '}<Link to={`${mainUrl}/integrations`} className="text-primary-500 underline font-semibold">
            here
          </Link>{' '}
          first to proceed with the test.
        </div>
      </div>
    </div>
  );
};

// Authentication Section
const AuthenticationSection = ({
  showAuthFields,
  setShowAuthFields,
  register,
  errors,
  watch,
  setValue,
  headerFields,
  appendHeader,
  removeHeader,
  replaceHeaders,
}) => {
  const currentHeaders = watch('customHeaders') || [];
  const canAddHeader = currentHeaders.length < 5;

  const handleAddHeader = () => {
    if (!canAddHeader) return;
    appendHeader({ headerName: '', headerValue: '' });
  };

  const handleRemoveHeader = (index) => {
    if (currentHeaders.length <= 1) {
      replaceHeaders([{ headerName: '', headerValue: '' }]);
      return;
    }
    removeHeader(index);
  };
  return (
    <div>
      <div className="flex items-center gap-2 my-2">
        <img src='/images/genpt/accesslevel.svg' alt="Access Level" className="w-6 h-6" />
        <div className="tracking-wide font-semibold text-black">Access Level</div>
      </div>
      <div>Select the level of access for this test</div>

      <div className="flex flex-col md:flex-row gap-4 mt-4">
          {/* UNAUTHENTICATED CARD */}
          <div
            onClick={() => {
              setShowAuthFields(false);
              setValue('cookieAuth', '');
              replaceHeaders([{ headerName: '', headerValue: '' }]);
            }}
            className={`cursor-pointer border rounded-lg px-5 py-4 w-[200px] text-center transition-all
              ${
                !showAuthFields
                  ? 'border-primary-500 bg-primary-50 shadow-md'
                  : 'border-gray-300 hover:border-primary-400 hover:shadow-sm'
              }`}
          >
            <div className="font-semibold text-black text-sm">Unauthenticated</div>
          </div>

          {/* AUTHENTICATED CARD */}
          <MuiTooltip
            title={
              <div className="text-xs">
                <div className="font-bold my-2">Test Behind Your Login Page</div>
                <div>
                  Enables authenticated vulnerability checks in internal areas such as dashboards,
                  settings, and sensitive data zones.
                </div>
                <div className="mt-2">
                  <span className="font-semibold">Best Practice:</span> Use a test account with
                  regular user permissions.
                </div>
              </div>
            }
          >
            <div
              onClick={() => {
                setShowAuthFields(true);
                if (!(watch('customHeaders') || []).length) {
                  replaceHeaders([{ headerName: '', headerValue: '' }]);
                }
              }}
              className={`cursor-pointer border rounded-lg px-5 py-4 w-[200px] text-center transition-all
              ${
                showAuthFields
                  ? 'border-primary-500 bg-primary-50 shadow-md'
                  : 'border-gray-300 hover:border-primary-400 hover:shadow-sm'
              }`}
            >
              <div className="font-semibold text-black text-sm">Authenticated</div>
            </div>
          </MuiTooltip>
        </div>

      {/* AUTH FIELDS SHOWN BELOW */}
      {showAuthFields && (
        <div>
          <div className="!my-4 border-t-2 border-[#EFEFEF]" />

          <div className="flex items-center gap-2 mb-2">
            <img src='/images/genpt/auth_details.svg' alt="Test Type" className="w-6 h-6" />
            <div className="tracking-wide font-semibold text-black">Authentication Details</div>
          </div>
          <div>Enter details for authenticated access using a cookie, custom headers, or both.</div>

          <FormControl component="fieldset" className="mt-6">
          <>
                <div className="mt-6">
                <div className="flex items-center gap-2 mb-2">
            <img src='/images/genpt/cookie.svg' alt="Test Type" className="w-6 h-6" />
            <div className="tracking-wide font-semibold text-black">Cookie Authentication</div>
          </div>
                  <div className="text-sm text-gray-600">
                  Paste the entire cookie string used for authenticated access.
                  </div>
                  <MuiTextField
                    label=""
                    placeholder="Example: sessionid=abc123; idp_token=xyz456"
                    InputProps={{
                      ...register('cookieAuth', {
                        validate: (value) =>
                          !value || value.length <= 2000 || 'Max length is 2000 characters',
                      }),
                    }}
                    error={Boolean(errors?.cookieAuth)}
                    helperText={
                      errors?.cookieAuth?.message
                    }
                  />
                </div>

                <div className="mt-6">
                <div className="flex items-center gap-2 mb-2">
            <img src='/images/genpt/header.svg' alt="Test Type" className="w-6 h-6" />
            <div className="tracking-wide font-semibold text-black">Custom Header Authentication</div>
          </div>
                  <div className="text-sm text-gray-600 mt-2">
                  Add up to 5 headers to include with your requests. Click + Add New to add key–value pairs.
                  </div>

                  {headerFields.map((field, index) => (
                    <div key={field.id} className="flex flex-col gap-2 mt-2">
                      <div className="flex flex-col md:flex-row gap-3">
                        <div className="flex-1">
                          <MuiTextField
                            label=""
                            placeholder="Header Name (e.g: Authorization)"
                            InputProps={{
                              ...register(`customHeaders.${index}.headerName`, {
                                validate: (value) =>
                                  !value || value.length <= 200 || 'Max length is 200 characters',
                              }),
                            }}
                          />
                        </div>
                        <div className="flex-1">
                          <MuiTextField
                            label=""
                            placeholder="Header Value (e.g.:Basic MTIz)"
                            InputProps={{
                              ...register(`customHeaders.${index}.headerValue`, {
                                validate: (value) =>
                                  !value || value.length <= 2000 || 'Max length is 2000 characters',
                              }),
                            }}
                          />
                        </div>
                        <div className="flex items-center">
                          {currentHeaders.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveHeader(index)}
                              className="text-sm text-red-600 hover:text-red-700 font-semibold"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="mt-4">
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={handleAddHeader}
                      disabled={!canAddHeader}
                      className="!text-primary-600 !border-primary-200 hover:!border-primary-400 disabled:!border-gray-200 disabled:!text-gray-400"
                    >
                      + Add New
                    </Button>
                    <div className="text-grey text-sm mt-2">
                <WarningAmberIcon fontSize="inherit" color="warning" className="mr-1" />
                <span className="text-[#ED6C02] font-semibold">Note:</span> If the provided header or cookie values are invalid or expired, test results may be inaccurate.
              </div>
                  </div>
                </div>
              </>
          </FormControl>
        </div>
      )}
    </div>
  );
};
